import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import MetaTags from '../components/MetaTags';
import api from '../utils/api';
import { FaPlay, FaPause } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Input from '../components/Input';
import ShareLinksModal from '../components/ShareLinksModal';
import PollModal from '../components/PollModal';
import PollCard from '../components/PollCard';
import DisplayPreview from '../components/DisplayPreview';
import PollSettingsModal from '../components/PollSettingsModal';
import TagManager from '../components/TagManager';
import CustomColumnModal from '../components/CustomColumnModal';
import AddQuestionModal from '../components/AddQuestionModal';
import BulkActionsBar from '../components/BulkActionsBar';
import QuestionTagModal from '../components/QuestionTagModal';
import PasswordAuth from '../components/PasswordAuth';
import QuestionCard from '../components/QuestionCard';
import { DEFAULT_LIVE_TEXT_SETTINGS, normalizeLiveTextSettings, hasLiveTextContent } from '../utils/liveTextSettings';
import { DEFAULT_QR_TEMPLATE, normalizeQrTemplate, setQrModeValues } from '../utils/qrTemplateSettings';

// Sortable and Droppable Column Component for Inbox Sidebar
const InboxSortableColumn = ({ column, isSelected, onSelect, isDragActive, onEditColumn, onDeleteColumn, questions, sessionId, onHoverChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `sortable-column-${column.id}` });
  
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `column-${column.id}`,
  });

  const [showContextMenu, setShowContextMenu] = useState(false);
  const contextMenuRef = useRef(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: column.bgColor === 'red' 
      ? (isSelected ? '#dc2626' : '#fecaca') 
      : column.bgColor === 'green' 
      ? (isSelected ? '#16a34a' : '#bbf7d0')
      : column.color ? `${column.color}30` : (isSelected ? '#2563eb' : '#f3f4f6'),
    backgroundImage: 'none'
  };

  // Don't combine refs - keep them separate to avoid conflicts
  // setNodeRef for sortable functionality
  // setDroppableRef for droppable functionality

  // Determine if this column is being hovered over during a question drag
  const isQuestionDragOver = isDragActive && isOver && !isDragging;
  
  
  // Notify parent when hover state changes
  useEffect(() => {
    if (onHoverChange && isDragActive) {
      onHoverChange(isOver);
    }
  }, [isOver, isDragActive, onHoverChange]);

  const handleContextMenu = (e) => {
    console.log('handleContextMenu called, current showContextMenu:', showContextMenu);
    e.preventDefault();
    e.stopPropagation();
    setShowContextMenu(prev => {
      console.log('Setting showContextMenu to:', !prev);
      return !prev;
    });
  };

  const handleEdit = () => {
    setShowContextMenu(false);
    onEditColumn(column);
  };

  const handleDelete = () => {
    console.log('Delete button clicked for column:', column);
    setShowContextMenu(false);
    onDeleteColumn(column);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setShowContextMenu(false);
      }
    };

    if (showContextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showContextMenu]);

  return (
    <div
      ref={setDroppableRef}
      style={style}
      className={`group relative rounded-lg transition-all duration-300 ease-out ${
        isSelected && !column.color
          ? 'bg-blue-600 text-white'
          : isSelected && column.color
          ? 'text-white'
          : isQuestionDragOver
          ? 'bg-green-100 text-green-800 border-2 border-green-400 shadow-xl transform scale-110 py-6 min-h-20 ring-4 ring-green-300 ring-opacity-60 animate-pulse'
          : isOver
          ? 'bg-blue-100 text-blue-800 border-2 border-blue-300 shadow-lg transform scale-105 py-4 ring-2 ring-blue-200 ring-opacity-50'
          : isDragging
          ? 'shadow-lg border-blue-300 bg-blue-50'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {/* Drag handle for column reordering - only show when not dragging a question and column can be moved */}
      {!isDragActive && column.canMove !== false && (
        <div
          ref={setNodeRef}
          {...attributes}
          {...listeners}
          className="absolute left-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move z-10"
        >
          <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
          </svg>
        </div>
      )}
      
      <div
        onClick={onSelect}
        className="w-full text-left px-8 py-2 rounded-lg transition-colors cursor-pointer"
      >
        <div className="flex items-center w-full">
          <span className="font-medium flex-grow">{column.name}</span>
          <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
            <span className={`text-sm ${
              isSelected ? 'text-white' : 'text-gray-500'
            }`}>
              {column.count}
            </span>
            {column.canEdit !== false && (
              <button
                onClick={(e) => {
                  console.log('Three dots clicked for column:', column);
                  handleContextMenu(e);
                }}
                className="p-1 hover:bg-black/10 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <div 
          ref={contextMenuRef}
          className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-32"
        >
          <button
            onClick={handleEdit}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};


// Sortable Question Card Component
const SortableQuestionCard = ({ question, status, onEdit, onDelete, isSelected, onToggleSelect, onTagClick, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: `${status}-${question.id}` });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`mb-3 ${isDragging ? 'opacity-30' : 'opacity-100'} transition-opacity duration-200`}
      id={`question-card-${status}-${question.id}`}
    >
      <QuestionCard
        question={question}
        status={status}
        onEdit={onEdit}
        onDelete={onDelete}
        onTagClick={onTagClick}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
        showCheckbox={true}
        showActions={true}
      />
    </div>
  );
};

const DroppableColumn = ({ id, children, className }) => {
  const { setNodeRef } = useDroppable({
    id
  });
  
  return (
    <div ref={setNodeRef} className={className}>
      {children}
    </div>
  );
};

// NameTagCard Component
const NameTagCard = ({ nameTag, onToggleOnAir, onEdit, onDelete, onUpdatePlacement, onClone }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const getPlacementIcon = (placement) => {
    switch (placement) {
      case 'top-left': return '↖';
      case 'top-right': return '↗';
      case 'bottom-left': return '↙';
      case 'bottom-right': return '↘';
      default: return '↖';
    }
  };

  const placementOptions = [
    { value: 'top-left', label: 'Top Left', icon: '↖' },
    { value: 'top-right', label: 'Top Right', icon: '↗' },
    { value: 'bottom-right', label: 'Bottom Right', icon: '↘' },
    { value: 'bottom-left', label: 'Bottom Left', icon: '↙' }
  ];

  const handleToggleOnAir = () => {
    onToggleOnAir(nameTag.id, !nameTag.is_on_air);
    setShowMenu(false);
  };

  const handleEdit = () => {
    onEdit(nameTag);
    setShowMenu(false);
  };

  const handleClone = () => {
    onClone(nameTag);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this name tag?')) {
      onDelete(nameTag.id);
      setShowMenu(false);
    }
  };

  const handlePositionCycle = () => {
    const currentPlacement = nameTag.placement || 'top-left';
    const currentIndex = placementOptions.findIndex(opt => opt.value === currentPlacement);
    const nextIndex = (currentIndex + 1) % placementOptions.length;
    const nextPlacement = placementOptions[nextIndex].value;
    onUpdatePlacement(nameTag.id, nextPlacement);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg shadow-lg border-2 transition-all hover:shadow-xl relative flex flex-col h-full ${
      nameTag.is_on_air ? 'border-red-500 bg-gradient-to-br from-red-50 to-red-100 shadow-red-200 shadow-lg' : 'border-gray-300 shadow-gray-200'
    }`}>
      {/* Status Corner Indicator */}
      {nameTag.is_on_air && (
        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-red-500 animate-pulse" title="ON AIR"></div>
      )}

      {/* Content Area - Compact Layout */}
      <div className="flex-grow">
        {/* Preview and Menu Row */}
        <div className="flex items-start space-x-3 mb-3">
          {/* Compact Preview */}
          <div 
            className="rounded-lg p-3 border-2 border-dashed border-gray-300 relative flex-1"
            style={{ 
              backgroundColor: nameTag.background_color || '#ffffff',
              textAlign: nameTag.placement?.includes('right') ? 'right' : 'left',
              minHeight: '60px'
            }}
          >
            <div 
              className="font-bold text-sm" 
              style={{ 
                fontFamily: 'Source Sans Pro, sans-serif',
                color: nameTag.name_color || '#000000'
              }}
            >
              {nameTag.name || 'Name'}
            </div>
            <div 
              className="text-xs" 
              style={{ 
                fontFamily: 'Source Sans Pro, sans-serif',
                color: nameTag.subtitle_color || '#666666'
              }}
            >
              {nameTag.subtitle || 'Subtitle'}
            </div>
            <div className="absolute top-1 right-1 text-xs text-gray-400">
              {getPlacementIcon(nameTag.placement)}
            </div>
          </div>

          {/* Three Dots Menu */}
          <div className="relative z-30" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="text-gray-400 hover:text-gray-600 focus:outline-none p-2 rounded-full hover:bg-gray-100 relative z-30"
              aria-label="Name tag options"
              style={{pointerEvents: 'auto'}}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            
            {showMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-40 border border-gray-200">
                <div className="py-1">
                  <button
                    onClick={handleEdit}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={handleClone}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Clone
                  </button>
                  <button
                    onClick={handleDelete}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-300 my-3"></div>

        {/* Actions */}
        <div className="flex items-center justify-between space-x-3">
          {/* On Air Button - Bigger with text */}
          <button
            onClick={handleToggleOnAir}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition-all duration-200 hover:scale-105 font-semibold ${
              nameTag.is_on_air 
                ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg' 
                : 'bg-gray-300 hover:bg-gray-400 text-gray-700 shadow-sm'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>On Air</span>
          </button>

          {/* Position Change Button - Cycles through positions on click */}
          <button
            onClick={handlePositionCycle}
            className="w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105 bg-gray-300 hover:bg-gray-400 text-gray-700 shadow-sm"
            title={`Position: ${placementOptions.find(opt => opt.value === (nameTag.placement || 'top-left'))?.label || 'Top Left'}. Click to cycle.`}
          >
            <span className="text-xl">{getPlacementIcon(nameTag.placement)}</span>
          </button>
        </div>
    </div>
  );
};

// FlowGridSettingsModal Component
const FlowGridSettingsModal = ({ isOpen, onClose, onSubmit, settings, onImageUpload }) => {
  const [formData, setFormData] = useState({
        backgroundColor: '#1a1a1a',
        backgroundType: 'color',
        backgroundImage: '',
        backgroundGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textColor: '#ffffff',
        frameBackground: '#2a2a2a',
        frameOpacity: 0.9,
        fontSize: 'text-xl',
        showShadow: false
  });

  useEffect(() => {
    // Only update form data when modal is open and we have valid settings
    if (isOpen && settings && Object.keys(settings).length > 0) {
      setFormData({
        backgroundColor: settings.backgroundColor || '#1a1a1a',
        backgroundType: settings.backgroundType || 'color',
        backgroundImage: settings.backgroundImage || '',
        backgroundGradient: settings.backgroundGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textColor: settings.textColor || '#ffffff',
        frameBackground: settings.frameBackground || '#2a2a2a',
        frameOpacity: settings.frameOpacity !== undefined ? settings.frameOpacity : 0.9,
        fontSize: settings.fontSize || 'text-xl',
        showShadow: settings.showShadow || false
      });
    }
  }, [settings, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              name === 'frameOpacity' ? parseFloat(value) : 
              value
    }));
  };

  const handleImageFileChange = (e) => {
    if (onImageUpload && e.target.files && e.target.files[0]) {
      onImageUpload(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            Flow Grid Settings
          </h2>
          <p className="text-sm text-gray-500 mt-1">Customize the appearance of the flow grid display.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Background Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Type
            </label>
            <select
              name="backgroundType"
              value={formData.backgroundType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="color">Solid Color</option>
              <option value="gradient">Gradient</option>
              <option value="image">Image</option>
            </select>
          </div>

          {/* Background Settings */}
          {formData.backgroundType === 'color' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Color
              </label>
              <input
                type="color"
                name="backgroundColor"
                value={formData.backgroundColor}
                onChange={handleChange}
                className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {formData.backgroundType === 'gradient' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Gradient
              </label>
              <input
                type="text"
                name="backgroundGradient"
                value={formData.backgroundGradient}
                onChange={handleChange}
                placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {formData.backgroundType === 'image' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Image
              </label>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Upload Image File
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="text-center text-gray-500 text-sm">OR</div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Image URL
                  </label>
                  <input
                    type="text"
                    name="backgroundImage"
                    value={formData.backgroundImage}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Text Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text Color
            </label>
            <input
              type="color"
              name="textColor"
              value={formData.textColor}
              onChange={handleChange}
              className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Frame Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frame Background Color
              </label>
              <input
                type="color"
                name="frameBackground"
                value={formData.frameBackground}
                onChange={handleChange}
                className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frame Opacity: {Math.round(formData.frameOpacity * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                name="frameOpacity"
                value={formData.frameOpacity}
                onChange={handleChange}
                className="w-full"
              />
            </div>
          </div>

          {/* Font Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Font Size
            </label>
            <select
              name="fontSize"
              value={formData.fontSize}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="text-sm">Small (14px)</option>
              <option value="text-base">Medium (16px)</option>
              <option value="text-lg">Large (18px)</option>
              <option value="text-xl">Extra Large (20px)</option>
              <option value="text-2xl">2X Large (24px)</option>
              <option value="text-3xl">3X Large (30px)</option>
              <option value="text-4xl">4X Large (36px)</option>
            </select>
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="showShadow"
                  checked={formData.showShadow}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="text-sm font-medium text-gray-700">Show Shadow</span>
              </label>
            </div>
            
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// LiveTextSettingsModal Component
const LiveTextSettingsModal = ({ isOpen, onClose, onSubmit, settings }) => {
  const [formData, setFormData] = useState({
    fontSize: 48,
    margin: 5,
    textColor: '#ffffff',
    backgroundColor: '#1a1a1a',
    verticalPosition: 'middle',
    backgroundOpacity: 100
  });

  useEffect(() => {
    // Only update form data when modal is open and we have valid settings
    if (isOpen && settings && Object.keys(settings).length > 0) {
      const normalized = normalizeLiveTextSettings(settings);
      setFormData({
        fontSize: normalized.fontSize,
        margin: normalized.margin,
        textColor: normalized.textColor,
        backgroundColor: normalized.backgroundColor,
        verticalPosition: normalized.verticalPosition,
        backgroundOpacity: normalized.backgroundOpacity
      });
    }
  }, [settings, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (type === 'number' || type === 'range') ? parseInt(value, 10) || 0 : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            Live Text Settings
          </h2>
          <p className="text-sm text-gray-500 mt-1">Customize the appearance of the live text display.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Font Size Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Font Size: {formData.fontSize}px
            </label>
            <input
              type="range"
              min="12"
              max="200"
              step="1"
              name="fontSize"
              value={formData.fontSize}
              onChange={handleChange}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>12px</span>
              <span>200px</span>
            </div>
          </div>

          {/* Margin Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Margin: {formData.margin}%
            </label>
            <input
              type="range"
              min="0"
              max="80"
              step="1"
              name="margin"
              value={formData.margin}
              onChange={handleChange}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>80%</span>
            </div>
          </div>

          {/* Vertical Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vertical Position
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, verticalPosition: 'top' }))}
                className={`px-4 py-3 text-sm font-medium rounded-md border-2 transition-colors ${
                  formData.verticalPosition === 'top'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  <span>Top</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, verticalPosition: 'middle' }))}
                className={`px-4 py-3 text-sm font-medium rounded-md border-2 transition-colors ${
                  formData.verticalPosition === 'middle'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                  </svg>
                  <span>Middle</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, verticalPosition: 'bottom' }))}
                className={`px-4 py-3 text-sm font-medium rounded-md border-2 transition-colors ${
                  formData.verticalPosition === 'bottom'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center space-y-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span>Bottom</span>
                </div>
              </button>
            </div>
          </div>

          {/* Color Pickers */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Color
              </label>
              <input
                type="color"
                name="textColor"
                value={formData.textColor}
                onChange={handleChange}
                className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Color
              </label>
              <input
                type="color"
                name="backgroundColor"
                value={formData.backgroundColor}
                onChange={handleChange}
                className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Background Opacity Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Opacity: {formData.backgroundOpacity}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              name="backgroundOpacity"
              value={formData.backgroundOpacity}
              onChange={handleChange}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0% transparent</span>
              <span>100% opaque</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              0% makes the page fully transparent for OBS, vMix, and other browser-source overlays. The text stays fully opaque. You can also add <code className="bg-gray-100 px-1 rounded">?transparent=1</code> or <code className="bg-gray-100 px-1 rounded">?opacity=0</code> to the live text URL.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const QR_SIZE_SLIDER_MAX = 800;
const QR_MODE_LABELS = {
  fullscreen: 'Fullscreen',
  'lower-third': 'Lower Third'
};

// QRSettingsModal Component
const QRSettingsModal = ({ isOpen, onClose, onSubmit, qrTemplate, socket, sessionId, onImageUpload }) => {
  const [formData, setFormData] = useState(() => normalizeQrTemplate(DEFAULT_QR_TEMPLATE));

  useEffect(() => {
    // Only update form data when modal is open and we have valid template data
    if (isOpen && qrTemplate && qrTemplate.id) {
      setFormData(normalizeQrTemplate(qrTemplate));
    }
  }, [qrTemplate, isOpen]);

  const activeMode = formData.display_mode === 'lower-third' ? 'lower-third' : 'fullscreen';
  const modeValues = formData.modes?.[activeMode] || DEFAULT_QR_TEMPLATE.modes[activeMode];
  const modeLabel = QR_MODE_LABELS[activeMode];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(normalizeQrTemplate(formData));
    onClose();
  };

  const handleDisplayModeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      display_mode: e.target.value === 'lower-third' ? 'lower-third' : 'fullscreen'
    }));
  };

  const handleModeFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const mode = prev.display_mode === 'lower-third' ? 'lower-third' : 'fullscreen';
      return {
        ...prev,
        modes: {
          ...prev.modes,
          [mode]: {
            ...prev.modes[mode],
            [name]: value
          }
        }
      };
    });
  };

  if (!isOpen) return null;

  const qrSizeNumber = Number(modeValues.qr_size);
  const qrSizeSliderValue = Number.isFinite(qrSizeNumber)
    ? Math.min(QR_SIZE_SLIDER_MAX, Math.max(1, qrSizeNumber))
    : 300;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            QR Code Settings — {modeLabel}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Editing {modeLabel} only. Fullscreen and Lower Third each keep their own size, position, colors, and styling.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Display Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Mode
            </label>
            <select
              name="display_mode"
              value={formData.display_mode}
              onChange={handleDisplayModeChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="fullscreen">Fullscreen - Full screen centered display</option>
              <option value="lower-third">Lower Third - Compact rounded frame</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.display_mode === 'fullscreen' 
                ? 'These controls apply only to Fullscreen. Switching modes loads that mode’s saved settings.' 
                : 'These controls apply only to Lower Third. Switching modes loads that mode’s saved settings.'}
            </p>
          </div>

          {/* Call to Action */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Call to Action Text ({modeLabel})
            </label>
            <input
              type="text"
              name="call_to_action"
              value={modeValues.call_to_action}
              onChange={handleModeFieldChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ask a question"
            />
            <p className="text-xs text-gray-500 mt-1">This text appears next to the QR code. Leave it empty to hide the text on the display.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horizontal Position (%)
              </label>
              <input
                type="number"
                name="position_x"
                value={modeValues.position_x}
                onChange={handleModeFieldChange}
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Decimals allowed. 0 = Left, 50 = Center, 100 = Right</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vertical Position (%)
              </label>
              <input
                type="number"
                name="position_y"
                value={modeValues.position_y}
                onChange={handleModeFieldChange}
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Decimals allowed. 0 = Top, 50 = Center, 85 = Lower Third, 100 = Bottom</p>
            </div>
          </div>

          {/* Background Settings - only for fullscreen mode */}
          {formData.display_mode === 'fullscreen' && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-md font-semibold text-gray-800">Background Settings (Fullscreen)</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Image
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Upload Image File
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (onImageUpload && e.target.files && e.target.files[0]) {
                          onImageUpload(e);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="text-center text-gray-500 text-sm">OR</div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Image URL
                    </label>
                    <input
                      type="text"
                      name="background_image"
                      value={modeValues.background_image || ''}
                      onChange={handleModeFieldChange}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {modeValues.background_image && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600 mb-1">Current background image:</p>
                      <img 
                        src={modeValues.background_image} 
                        alt="Background preview" 
                        className="max-w-full h-32 object-cover rounded border border-gray-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  If a background image is set, it will be used instead of the background color.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="background_color"
                  value={modeValues.background_color}
                  onChange={handleModeFieldChange}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  name="background_color"
                  value={modeValues.background_color}
                  onChange={handleModeFieldChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#1e3a8a"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="text_color"
                  value={modeValues.text_color}
                  onChange={handleModeFieldChange}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  name="text_color"
                  value={modeValues.text_color}
                  onChange={handleModeFieldChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#ffffff"
                />
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Opacity: {modeValues.background_opacity}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                name="background_opacity"
                value={modeValues.background_opacity}
                onChange={handleModeFieldChange}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0% transparent</span>
                <span>100% opaque</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Affects only the {modeLabel.toLowerCase()} background. The text and QR code stay fully opaque.
              </p>
            </div>
          </div>

          {/* QR Code Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              QR Code Size (pixels)
            </label>
            <input
              type="number"
              name="qr_size"
              value={modeValues.qr_size}
              onChange={handleModeFieldChange}
              min="1"
              step="any"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="range"
              min="1"
              max={QR_SIZE_SLIDER_MAX}
              step="1"
              name="qr_size"
              value={qrSizeSliderValue}
              onChange={handleModeFieldChange}
              className="w-full mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Any positive size is applied exactly for {modeLabel}. The slider is a shortcut up to {QR_SIZE_SLIDER_MAX}px; type a larger value in the box if you need it.
            </p>
          </div>

          <div className="space-y-4 border-t pt-4">
            <h3 className="text-md font-semibold text-gray-800">{modeLabel} Styling</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Border Radius (px)
                </label>
                <input
                  type="number"
                  name="border_radius"
                  value={modeValues.border_radius}
                  onChange={handleModeFieldChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Padding (px)
                </label>
                <input
                  type="number"
                  name="padding"
                  value={modeValues.padding}
                  onChange={handleModeFieldChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Shadow Settings</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shadow Blur (px)
                  </label>
                  <input
                    type="number"
                    name="shadow_blur"
                    value={modeValues.shadow_blur}
                    onChange={handleModeFieldChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shadow Opacity
                  </label>
                  <input
                    type="number"
                    name="shadow_opacity"
                    value={modeValues.shadow_opacity}
                    onChange={handleModeFieldChange}
                    min="0"
                    max="1"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shadow Offset X (px)
                  </label>
                  <input
                    type="number"
                    name="shadow_offset_x"
                    value={modeValues.shadow_offset_x}
                    onChange={handleModeFieldChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shadow Offset Y (px)
                  </label>
                  <input
                    type="number"
                    name="shadow_offset_y"
                    value={modeValues.shadow_offset_y}
                    onChange={handleModeFieldChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Custom CSS */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom CSS (Advanced, {modeLabel} only)
            </label>
            <textarea
              name="custom_css"
              value={modeValues.custom_css}
              onChange={handleModeFieldChange}
              rows="6"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder=".qr-call-to-action { font-size: 80px; }"
            />
            <p className="text-xs text-gray-500 mt-1">
              Override default styles. Use classes: .qr-display-container, .qr-text-section, .qr-call-to-action, .qr-code-section, .qr-lower-third-container
            </p>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// NameTagSettingsModal Component
const NameTagSettingsModal = ({ isOpen, onClose, onSubmit, settings }) => {
  const [formData, setFormData] = useState({
    background_color: '#ffffff',
    name_color: '#000000',
    subtitle_color: '#666666',
    name_font_size: '24px',
    subtitle_font_size: '16px',
    name_font_weight: 'bold',
    subtitle_font_weight: 'normal',
    padding: '12px 20px',
    borderRadius: '8px',
    minWidth: '300px',
    maxWidth: '300px',
    subtitle_margin_top: '0px'
  });

  useEffect(() => {
    // Only update form data when modal is open and we have valid settings
    if (isOpen && settings && Object.keys(settings).length > 0) {
      setFormData({
        background_color: settings.background_color || '#ffffff',
        name_color: settings.name_color || '#000000',
        subtitle_color: settings.subtitle_color || '#666666',
        name_font_size: settings.name_font_size || '24px',
        subtitle_font_size: settings.subtitle_font_size || '16px',
        name_font_weight: settings.name_font_weight || 'bold',
        subtitle_font_weight: settings.subtitle_font_weight || 'normal',
        padding: settings.padding || '12px 20px',
        borderRadius: settings.borderRadius || '8px',
        minWidth: settings.minWidth || '300px',
        maxWidth: settings.maxWidth || '300px',
        subtitle_margin_top: settings.subtitle_margin_top || '0px'
      });
    }
  }, [settings, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fontWeightOptions = ['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            Name Tag Settings
          </h2>
          <p className="text-sm text-gray-500 mt-1">Configure default settings for all name tags. These can be overridden per name tag.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Colors Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-gray-800 border-b pb-2">Colors</h3>
            
            {/* Background Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="background_color"
                  value={formData.background_color}
                  onChange={handleChange}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.background_color}
                  onChange={handleChange}
                  name="background_color"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#ffffff"
                />
              </div>
            </div>

            {/* Name Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="name_color"
                  value={formData.name_color}
                  onChange={handleChange}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.name_color}
                  onChange={handleChange}
                  name="name_color"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#000000"
                />
              </div>
            </div>

            {/* Subtitle Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtitle Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="subtitle_color"
                  value={formData.subtitle_color}
                  onChange={handleChange}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.subtitle_color}
                  onChange={handleChange}
                  name="subtitle_color"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#666666"
                />
              </div>
            </div>
          </div>

          {/* Typography Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-gray-800 border-b pb-2">Typography</h3>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Name Font Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name Font Size
                </label>
                <input
                  type="text"
                  name="name_font_size"
                  value={formData.name_font_size}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="24px"
                />
              </div>

              {/* Name Font Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name Font Weight
                </label>
                <select
                  name="name_font_weight"
                  value={formData.name_font_weight}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {fontWeightOptions.map(weight => (
                    <option key={weight} value={weight}>{weight}</option>
                  ))}
                </select>
              </div>

              {/* Subtitle Font Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtitle Font Size
                </label>
                <input
                  type="text"
                  name="subtitle_font_size"
                  value={formData.subtitle_font_size}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="16px"
                />
              </div>

              {/* Subtitle Font Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtitle Font Weight
                </label>
                <select
                  name="subtitle_font_weight"
                  value={formData.subtitle_font_weight}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {fontWeightOptions.map(weight => (
                    <option key={weight} value={weight}>{weight}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Layout Section */}
          <div className="space-y-4">
            <h3 className="text-md font-semibold text-gray-800 border-b pb-2">Layout</h3>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Padding */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Padding
                </label>
                <input
                  type="text"
                  name="padding"
                  value={formData.padding}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="12px 20px"
                />
              </div>

              {/* Border Radius */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Border Radius
                </label>
                <input
                  type="text"
                  name="borderRadius"
                  value={formData.borderRadius}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="8px"
                />
              </div>

              {/* Min Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Width
                </label>
                <input
                  type="text"
                  name="minWidth"
                  value={formData.minWidth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="300px"
                />
              </div>

              {/* Max Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Width
                </label>
                <input
                  type="text"
                  name="maxWidth"
                  value={formData.maxWidth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="300px"
                />
              </div>

              {/* Subtitle Margin Top (distance between headline and subtitle) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Headline to Subtitle Distance
                </label>
                <input
                  type="text"
                  name="subtitle_margin_top"
                  value={formData.subtitle_margin_top}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0px"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div 
              className="rounded-lg p-4 border-2 border-dashed border-gray-300 relative"
              style={{ 
                backgroundColor: formData.background_color,
                padding: formData.padding,
                borderRadius: formData.borderRadius,
                minWidth: formData.minWidth,
                maxWidth: formData.maxWidth,
                textAlign: 'left'
              }}
            >
              <div 
                style={{ 
                  fontFamily: 'Source Sans Pro, sans-serif',
                  color: formData.name_color,
                  fontSize: formData.name_font_size,
                  fontWeight: formData.name_font_weight
                }}
              >
                Sample Name
              </div>
              <div 
                style={{ 
                  fontFamily: 'Source Sans Pro, sans-serif',
                  color: formData.subtitle_color,
                  fontSize: formData.subtitle_font_size,
                  fontWeight: formData.subtitle_font_weight,
                  marginTop: formData.subtitle_margin_top || '0px'
                }}
              >
                Sample Subtitle
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// NameTagModal Component
const NameTagModal = ({ isOpen, onClose, onSubmit, nameTag, globalSettings }) => {
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    subtitle: '',
    placement: 'top-left',
    background_color: null,
    name_color: null,
    subtitle_color: null,
    useGlobalSettings: true
  });

  useEffect(() => {
    const defaults = {
      background_color: globalSettings?.background_color || '#ffffff',
      name_color: globalSettings?.name_color || '#000000',
      subtitle_color: globalSettings?.subtitle_color || '#666666'
    };

    if (nameTag) {
      // Check if any color is overridden (not null and not undefined)
      const hasColorOverrides = 
        (nameTag.background_color !== null && nameTag.background_color !== undefined) ||
        (nameTag.name_color !== null && nameTag.name_color !== undefined) ||
        (nameTag.subtitle_color !== null && nameTag.subtitle_color !== undefined);
      
      setFormData({
        id: nameTag.id,
        name: nameTag.name || '',
        subtitle: nameTag.subtitle || '',
        placement: nameTag.placement || 'top-left',
        background_color: nameTag.background_color !== null && nameTag.background_color !== undefined 
          ? nameTag.background_color 
          : defaults.background_color,
        name_color: nameTag.name_color !== null && nameTag.name_color !== undefined 
          ? nameTag.name_color 
          : defaults.name_color,
        subtitle_color: nameTag.subtitle_color !== null && nameTag.subtitle_color !== undefined 
          ? nameTag.subtitle_color 
          : defaults.subtitle_color,
        useGlobalSettings: !hasColorOverrides // Default to true (use global) unless colors are explicitly overridden
      });
    } else {
      setFormData({
        name: '',
        subtitle: '',
        placement: 'top-left',
        background_color: defaults.background_color,
        name_color: defaults.name_color,
        subtitle_color: defaults.subtitle_color,
        useGlobalSettings: true // Always default to true for new name tags
      });
    }
  }, [nameTag, isOpen, globalSettings]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // If using global settings, send null values to indicate override
    const submitData = formData.useGlobalSettings 
      ? {
          ...formData,
          background_color: null,
          name_color: null,
          subtitle_color: null
        }
      : formData;
    onSubmit(submitData);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const placementOptions = [
    { value: 'top-left', label: 'Top Left', icon: '↖' },
    { value: 'top-right', label: 'Top Right', icon: '↗' },
    { value: 'bottom-left', label: 'Bottom Left', icon: '↙' },
    { value: 'bottom-right', label: 'Bottom Right', icon: '↘' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {nameTag ? 'Edit Name Tag' : 'Create Name Tag'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter name"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subtitle
            </label>
            <input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter subtitle"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            />
          </div>

          {/* Placement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Placement *
            </label>
            <div className="grid grid-cols-2 gap-2">
              {placementOptions.map(option => (
                <label
                  key={option.value}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.placement === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="placement"
                    value={option.value}
                    checked={formData.placement === option.value}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{option.icon}</span>
                    <span className="text-sm font-medium">{option.label}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Use Global Settings Toggle */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.useGlobalSettings}
                onChange={(e) => {
                  const useGlobal = e.target.checked;
                  const defaults = {
                    background_color: globalSettings?.background_color || '#ffffff',
                    name_color: globalSettings?.name_color || '#000000',
                    subtitle_color: globalSettings?.subtitle_color || '#666666'
                  };
                  setFormData(prev => ({
                    ...prev,
                    useGlobalSettings: useGlobal,
                    background_color: useGlobal ? defaults.background_color : prev.background_color,
                    name_color: useGlobal ? defaults.name_color : prev.name_color,
                    subtitle_color: useGlobal ? defaults.subtitle_color : prev.subtitle_color
                  }));
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Use Global Settings (overrides can be set below)
              </span>
            </label>
          </div>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color {formData.useGlobalSettings && <span className="text-xs text-gray-500">(from global settings)</span>}
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                name="background_color"
                value={formData.background_color}
                onChange={handleChange}
                disabled={formData.useGlobalSettings}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <input
                type="text"
                value={formData.background_color}
                onChange={handleChange}
                name="background_color"
                disabled={formData.useGlobalSettings}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="#ffffff"
              />
            </div>
          </div>

          {/* Text Colors */}
          <div className="space-y-4">
            {/* Name Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="name_color"
                  value={formData.name_color}
                  onChange={handleChange}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.name_color}
                  onChange={handleChange}
                  name="name_color"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#000000"
                />
              </div>
            </div>

            {/* Subtitle Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtitle Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="subtitle_color"
                  value={formData.subtitle_color}
                  onChange={handleChange}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.subtitle_color}
                  onChange={handleChange}
                  name="subtitle_color"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#666666"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div 
              className="rounded-lg p-4 border-2 border-dashed border-gray-300 relative"
              style={{ 
                backgroundColor: formData.background_color,
                textAlign: formData.placement.includes('right') ? 'right' : 'left'
              }}
            >
              <div 
                className="font-bold text-lg" 
                style={{ 
                  fontFamily: 'Source Sans Pro, sans-serif',
                  color: formData.name_color
                }}
              >
                {formData.name || 'Name'}
              </div>
              <div 
                className="text-sm" 
                style={{ 
                  fontFamily: 'Source Sans Pro, sans-serif',
                  color: formData.subtitle_color
                }}
              >
                {formData.subtitle || 'Subtitle'}
              </div>
              <div className="absolute top-1 right-1 text-xs text-gray-400">
                {placementOptions.find(opt => opt.value === formData.placement)?.icon}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {nameTag ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const QuestionManager = () => {
  const { sessionId } = useParams();
  const { session, socket, joinSession, authenticateWithPassword } = useSession();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [questions, setQuestions] = useState({
    new: [],
    selected: [],
    on_air: [],
    done: []
  });
  
  // Polls state
  const [polls, setPolls] = useState([]);
  const [showPollModal, setShowPollModal] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [pollsSectionCollapsed, setPollsSectionCollapsed] = useState(false);
  const [showPollSettingsModal, setShowPollSettingsModal] = useState(false);
  
  // Name Tags state
  const [nameTags, setNameTags] = useState([]);
  const [showNameTagModal, setShowNameTagModal] = useState(false);
  const [editingNameTag, setEditingNameTag] = useState(null);
  const [nameTagsSectionCollapsed, setNameTagsSectionCollapsed] = useState(false);
  const [nameTagSettings, setNameTagSettings] = useState({
    background_color: '#ffffff',
    name_color: '#000000',
    subtitle_color: '#666666',
    name_font_size: '24px',
    subtitle_font_size: '16px',
    name_font_weight: 'bold',
    subtitle_font_weight: 'normal',
    padding: '12px 20px',
    borderRadius: '8px',
    minWidth: '300px',
    maxWidth: '300px',
    subtitle_margin_top: '0px'
  });
  const [showNameTagSettingsModal, setShowNameTagSettingsModal] = useState(false);
  
  // Display preview state
  const [showDisplayPreview, setShowDisplayPreview] = useState(false);
  
  // Create a session object that will be used by Layout if the actual session isn't available
  const [localSession, setLocalSession] = useState(null);

  // New features state
  const [tags, setTags] = useState([]);
  const [customColumns, setCustomColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState(['new', 'selected', 'on_air', 'done']);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('manual'); // Legacy state (manual only now)
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [showTagManager, setShowTagManager] = useState(false);
  const [showCustomColumnModal, setShowCustomColumnModal] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [showQuestionTagModal, setShowQuestionTagModal] = useState(false);
  const [tagModalQuestionId, setTagModalQuestionId] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showMainAddDropdown, setShowMainAddDropdown] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    // Get view mode from localStorage, default to 'kanban'
    const savedViewMode = localStorage.getItem('questionManagerViewMode');
    return savedViewMode || 'kanban';
  }); // 'kanban', 'list', or 'inbox'
  const [selectedInboxColumn, setSelectedInboxColumn] = useState(() => {
    // Get selected inbox column from localStorage, default to 'new'
    const savedInboxColumn = localStorage.getItem('questionManagerInboxColumn');
    return savedInboxColumn || 'new';
  }); // For inbox view
  
  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('questionManagerViewMode', viewMode);
  }, [viewMode]);
  
  // Save selected inbox column to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('questionManagerInboxColumn', selectedInboxColumn);
  }, [selectedInboxColumn]);
  const [isQuestionsFullScreen, setIsQuestionsFullScreen] = useState(false); // For full screen questions management
  const questionsSectionRef = useRef(null);
  const addDropdownRef = useRef(null);
  const mainAddDropdownRef = useRef(null);
  const [animationState, setAnimationState] = useState('normal'); // 'normal', 'expanding', 'fullscreen', 'collapsing'
  
  // Questions section height management
  const [questionsSectionHeight, setQuestionsSectionHeight] = useState('600px');
  
  // Track when hovering over any droppable area for question scaling
  const [isHoveringOverDroppable, setIsHoveringOverDroppable] = useState(false);
  
  // Callback for when hover state changes on any column
  const handleColumnHoverChange = (isHovering) => {
    // Only set to true if hovering, but don't immediately set to false
    // The false will be handled by the drag end event
    if (isHovering) {
      setIsHoveringOverDroppable(true);
    }
  };
  
  // Force drag and drop recalculation when layout changes
  useEffect(() => {
    if (animationState === 'fullscreen') {
      // Small delay to ensure layout has settled
      const timer = setTimeout(() => {
        // Force a re-render by updating a dummy state
        setQuestionsSectionHeight(prev => prev);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [animationState]);
  const [showFadeOut, setShowFadeOut] = useState(false);

  // Handle fade-out effect when content overflows
  useEffect(() => {
    const checkOverflow = () => {
      if (questionsSectionRef.current) {
        const element = questionsSectionRef.current;
        const hasOverflow = element.scrollHeight > element.clientHeight;
        setShowFadeOut(hasOverflow);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    
    return () => window.removeEventListener('resize', checkOverflow);
  }, [questions, viewMode, selectedInboxColumn]);

  // Handle animated full screen toggle
  const handleFullScreenToggle = () => {
    if (isQuestionsFullScreen) {
      // Collapsing
      setAnimationState('collapsing');
      setTimeout(() => {
        setIsQuestionsFullScreen(false);
        setAnimationState('normal');
      }, 300);
    } else {
      // Expanding
      setAnimationState('expanding');
      setIsQuestionsFullScreen(true);
      setTimeout(() => {
        setAnimationState('fullscreen');
      }, 500);
    }
  };

  // Handle editing a column from inbox view
  const handleEditColumn = (column) => {
    // If it's a custom column, find the original column object
    if (column.id.startsWith('custom_')) {
      const originalColumnId = column.id.replace('custom_', '');
      const originalColumn = customColumns.find(col => col.id.toString() === originalColumnId);
      setEditingColumn(originalColumn);
    } else {
      // For standard columns, we can't edit them
      setError('Standard columns cannot be edited');
      return;
    }
    setShowCustomColumnModal(true);
  };

  // Handle deleting a column from inbox view
  const handleDeleteInboxColumn = async (column) => {
    console.log('handleDeleteInboxColumn called with column:', column);
    console.log('Current questions state:', questions);
    console.log('Questions in column:', questions[`custom_${column.id}`]);
    
    if (window.confirm(`Are you sure you want to delete the column "${column.name}"? This will move all questions in this column to the "New" column.`)) {
      console.log('User confirmed deletion, proceeding...');
      try {
        // First, move all questions from this column to 'new'
        const questionsInColumn = questions[column.id] || [];
        
        if (questionsInColumn.length > 0) {
          console.log(`Moving ${questionsInColumn.length} questions to 'new' status...`);
          // Move each question to 'new' status
          for (const question of questionsInColumn) {
            console.log(`Moving question ${question.id} to 'new' status`);
            await api.put(`/api/questions/${question.id}/status`, {
              status: 'new',
              sessionId
            });
          }
        }
        
        // Then delete the column
        const columnId = column.id.replace('custom_', '');
        console.log(`Deleting column ${columnId}...`);
        await api.delete(`/api/custom-columns/${columnId}`);
        console.log('Column deleted successfully from API');
        
        // Update local state
        setCustomColumns(prev => prev.filter(col => col.id.toString() !== columnId));
        
        // Remove from column order
        setColumnOrder(prev => prev.filter(id => id !== column.id));
        
        // Update questions state
        setQuestions(prev => {
          const newQuestions = { ...prev };
          const questionsInColumn = prev[column.id] || [];
          
          // Add questions to 'new' column
          newQuestions.new = [...(newQuestions.new || []), ...questionsInColumn.map(q => ({ ...q, status: 'new' }))];
          
          // Remove the custom column
          delete newQuestions[column.id];
          
          return newQuestions;
        });
        
        // If this was the selected column, switch to 'new'
        if (selectedInboxColumn === column.id) {
          setSelectedInboxColumn('new');
        }
        
        setSuccess('Column deleted successfully');
      } catch (error) {
        console.error('Error deleting column:', error);
        setError('Failed to delete column');
      }
    }
  };
  const [qrTemplate, setQrTemplate] = useState(null);
  const [showQRSettingsModal, setShowQRSettingsModal] = useState(false);
  const [reactionsEnabled, setReactionsEnabled] = useState(false);
  const [reactionIcons, setReactionIcons] = useState(['❤️', '👍', '👎']);
  const [flowGridSettings, setFlowGridSettings] = useState({
    sourceColumn: 'new',
    backgroundColor: '#1a1a1a',
    backgroundType: 'color',
    backgroundImage: '',
    backgroundGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    textColor: '#ffffff',
    frameBackground: '#2a2a2a',
    frameOpacity: 0.9,
    fontSize: 'text-xl',
    scrollingEnabled: true
  });
  const [showFlowGridSettingsModal, setShowFlowGridSettingsModal] = useState(false);
  const [liveTextSettings, setLiveTextSettings] = useState(DEFAULT_LIVE_TEXT_SETTINGS);
  const [showLiveTextSettingsModal, setShowLiveTextSettingsModal] = useState(false);
  const [showLiveTextSection, setShowLiveTextSection] = useState(false);
  const [liveTextInput, setLiveTextInput] = useState('');
  const [paginationState, setPaginationState] = useState({}); // Per-column pagination
  const [listSortBy, setListSortBy] = useState({ field: 'created_at', order: 'desc' }); // List view sorting
  const [isQRCodeShowing, setIsQRCodeShowing] = useState(false); // Track QR code display state
  
  // Set up global handler for tagging questions
  useEffect(() => {
    window.tagQuestionHandler = (questionId) => {
      setTagModalQuestionId(questionId);
      setShowQuestionTagModal(true);
    };
    
    return () => {
      delete window.tagQuestionHandler;
    };
  }, []);

  // Fetch QR template on component mount and when modal opens
  const fetchQrTemplate = async () => {
    if (!sessionId) return;
    try {
      const response = await api.get(`/api/qr-templates/session/${sessionId}`);
      setQrTemplate(normalizeQrTemplate(response.data));
    } catch (error) {
      console.error('Error fetching QR template:', error);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchQrTemplate();
    }
  }, [sessionId]);

  // Refetch QR template when modal opens
  useEffect(() => {
    if (showQRSettingsModal && sessionId) {
      fetchQrTemplate();
    }
  }, [showQRSettingsModal, sessionId]);

  // Fetch reactions settings on component mount
  useEffect(() => {
    const fetchReactionsSettings = async () => {
      try {
        const response = await api.get(`/api/sessions/${sessionId}`);
        const session = response.data.session;
        if (session) {
          setReactionsEnabled(Boolean(session.reactions_enabled));
          if (session.reaction_icons) {
            try {
              const icons = JSON.parse(session.reaction_icons);
              setReactionIcons(icons);
            } catch (e) {
              console.error('Error parsing reaction icons:', e);
            }
          }
          
          // Fetch flow grid settings
          if (session.flow_grid_settings) {
            try {
              const settings = JSON.parse(session.flow_grid_settings);
              // Ensure scrollingEnabled defaults to true if not set
              setFlowGridSettings({
                ...settings,
                scrollingEnabled: settings.scrollingEnabled !== undefined ? settings.scrollingEnabled : true
              });
            } catch (e) {
              console.error('Error parsing flow grid settings:', e);
            }
          }
          
          // Fetch live text settings
          if (session.live_text_settings) {
            try {
              const settings = JSON.parse(session.live_text_settings);
              const loadedSettings = normalizeLiveTextSettings(settings);
              setLiveTextSettings(loadedSettings);
              setLiveTextInput(loadedSettings.text);
            } catch (e) {
              console.error('Error parsing live text settings:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching reactions settings:', error);
      }
    };

    if (sessionId) {
      fetchReactionsSettings();
    }
  }, [sessionId]);

  // Auto-join session if we have a sessionId in the URL but no active session
  useEffect(() => {
    const autoJoinSession = async () => {
      if (sessionId && !session && !localSession) {
        console.log(`Auto-joining session with ID: ${sessionId}`);
        try {
          // Try to join the session directly with the ID
          await joinSession({ id: sessionId });
          console.log(`Successfully auto-joined session: ${sessionId}`);
        } catch (error) {
          console.log(`Failed to auto-join session, falling back to local session: ${error.message}`);
          
          // Store session ID in localStorage for persistence
          localStorage.setItem('sessionId', sessionId);
          
          // Fetch session info to get the name
          try {
            const response = await api.get(`/api/sessions/${sessionId}`);
            if (response.data && response.data.session && response.data.session.name) {
              localStorage.setItem('sessionName', response.data.session.name);
            } else {
              localStorage.setItem('sessionName', 'Live Q&A Session');
            }
          } catch (err) {
            console.log('Error fetching session name, using default');
            localStorage.setItem('sessionName', 'Live Q&A Session');
          }
          
          // Create a local session object
          setLocalSession({
            id: sessionId,
            name: localStorage.getItem('sessionName') || 'Live Q&A Session',
          });
        }
      }
    };
    
    autoJoinSession();
  }, [sessionId, session, joinSession, localSession]);
  
  useEffect(() => {
    // If we have a session ID but no session, fetch session details and create a local session object
    const fetchSessionInfo = async () => {
      if (sessionId && !session) {
        try {
          const response = await api.get(`/api/sessions/${sessionId}`);
          const sessionName = response.data && response.data.session && response.data.session.name 
            ? response.data.session.name 
            : "Live Q&A Session";
            
          setLocalSession({
            id: sessionId,
            name: sessionName,
          });
        } catch (error) {
          console.log("Unable to fetch session name, using default");
          setLocalSession({
            id: sessionId,
            name: "Live Q&A Session",
          });
        }
      } else if (session) {
        // Use the session from context if available
        setLocalSession(null);
      }
    };
    
    fetchSessionInfo();
  }, [session, sessionId]);

  // Fetch session QR code visibility state
  useEffect(() => {
    const fetchSessionQRState = async () => {
      if (sessionId) {
        try {
          const response = await api.get(`/api/sessions/${sessionId}`);
          const session = response.data.session;
          if (session && session.qr_code_visible) {
            console.log('QR code was visible before refresh, restoring state in QuestionManager');
            setIsQRCodeShowing(true);
          }
        } catch (err) {
          console.error('Error fetching session QR state:', err);
        }
      }
    };
    
    fetchSessionQRState();
  }, [sessionId]);

  const [newQuestion, setNewQuestion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const recentlyAddedQuestions = useRef(new Set());
  
  // State for editing questions
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editContent, setEditContent] = useState('');
const [showEditModal, setShowEditModal] = useState(false);
const [editError, setEditError] = useState(null);
const [showShareModal, setShowShareModal] = useState(false);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [showCogwheelMenu, setShowCogwheelMenu] = useState(false);
  const [dragPreviewSize, setDragPreviewSize] = useState({ width: null, height: null });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside search popup
      if (showSearchPopup && !event.target.closest('[data-search-popup]')) {
        setShowSearchPopup(false);
      }
      // Check if click is outside cogwheel menu
      if (showCogwheelMenu && !event.target.closest('[data-cogwheel-menu]')) {
        setShowCogwheelMenu(false);
      }
      // Check if click is outside add dropdown
      if (showAddDropdown && addDropdownRef.current && !addDropdownRef.current.contains(event.target)) {
        setShowAddDropdown(false);
      }
      // Check if click is outside main add dropdown
      if (showMainAddDropdown && mainAddDropdownRef.current && !mainAddDropdownRef.current.contains(event.target)) {
        setShowMainAddDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchPopup, showCogwheelMenu, showAddDropdown, showMainAddDropdown]);

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a more intentional drag to start
      activationConstraint: {
        distance: 8, // 8px
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      // Require a more intentional drag to start
      activationConstraint: {
        delay: 250, // ms
        tolerance: 5, // px
      },
    })
  );

  // Define fetchPolls function outside useEffect so it can be called from other functions
  const fetchPolls = async () => {
    try {
      console.log('Fetching polls for session:', sessionId);
      console.log('Session ID type:', typeof sessionId);
      const response = await api.get(`/api/polls/session/${sessionId}`);
      console.log('Polls fetched:', response.data);
      console.log('Number of polls:', response.data.length);
      
      // Backend already parses options JSON, so we don't need to parse again
      const polls = response.data.map(poll => ({
        ...poll,
        // options are already parsed by the backend
        is_on_air: Boolean(poll.is_on_air),
        is_asked: Boolean(poll.is_asked),
        displayMode: poll.display_mode || 'question' // Use display_mode from database
      }));
      
      // Fetch results for each poll
      const pollsWithResults = await Promise.all(
        polls.map(async (poll) => {
          try {
            const resultsResponse = await api.get(`/api/polls/${poll.id}/with-results`);
            return {
              ...poll,
              results: resultsResponse.data.results || []
            };
          } catch (err) {
            console.error(`Error fetching results for poll ${poll.id}:`, err);
            return {
              ...poll,
              results: []
            };
          }
        })
      );
      
      setPolls(pollsWithResults);
    } catch (error) {
      console.error('Error fetching polls:', error);
    }
  };

  // Fetch name tags for the session
  const fetchNameTags = async () => {
    try {
      console.log('Fetching name tags for session:', sessionId);
      const response = await api.get(`/api/name-tags/session/${sessionId}`);
      console.log('Name tags fetched:', response.data);
      setNameTags(response.data);
    } catch (error) {
      console.error('Error fetching name tags:', error);
    }
  };

  // Fetch name tag settings for the session
  const fetchNameTagSettings = async () => {
    if (!sessionId) return;
    try {
      const response = await api.get(`/api/sessions/${sessionId}/name-tag-settings`);
      if (response.data.settings) {
        setNameTagSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching name tag settings:', error);
    }
  };

  // Fetch name tag settings when modal opens
  useEffect(() => {
    if (showNameTagSettingsModal && sessionId) {
      fetchNameTagSettings();
    }
  }, [showNameTagSettingsModal, sessionId]);

  // Handle name tag settings update
  const handleNameTagSettingsUpdate = async (settings) => {
    try {
      console.log('Updating name tag settings:', settings);
      console.log('Session ID:', sessionId);
      const response = await api.put(`/api/sessions/${sessionId}/name-tag-settings`, settings);
      console.log('Name tag settings update response:', response);
      
      // Use response data to ensure we have what was actually saved
      const savedSettings = response.data.settings || settings;
      setNameTagSettings(savedSettings);
      
      // Emit socket event
      if (socket) {
        socket.emit('name-tag-settings-updated', {
          settings: savedSettings,
          sessionId: sessionId
        });
      }
      
      setSuccess('Name tag settings updated successfully');
    } catch (error) {
      console.error('Error updating name tag settings:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update name tag settings';
      setError(errorMessage);
    }
  };

  // Fetch tags for the session
  const fetchTags = async () => {
    try {
      const response = await api.get(`/api/tags/session/${sessionId}`);
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch custom columns for the session
  const fetchCustomColumns = async () => {
    try {
      const response = await api.get(`/api/custom-columns/session/${sessionId}`);
      const columns = response.data;
      setCustomColumns(columns);
      
      // Initialize questions state with custom column keys
      setQuestions(prev => {
        const updated = { ...prev };
        columns.forEach(column => {
          const key = `custom_${column.id}`;
          if (!updated[key]) {
            updated[key] = [];
          }
        });
        return updated;
      });
    } catch (error) {
      console.error('Error fetching custom columns:', error);
    }
  };

  // Check authentication on component mount
  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if we have a valid session token
        const token = localStorage.getItem('sessionToken');
        const storedSessionId = localStorage.getItem('sessionId');
        const storedPassword = localStorage.getItem('sessionPassword');
        
        // Set the Authorization header if we have a token
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        if (token && storedSessionId === sessionId) {
          // We have stored credentials, verify they're still valid
          try {
            // Set the Authorization header for this request
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const response = await api.get('/api/sessions/me');
            if (response.data && response.data.session) {
              setIsAuthenticated(true);
              setIsCheckingAuth(false);
              return;
            }
          } catch (err) {
            // Token is invalid, clear stored data
            localStorage.removeItem('sessionToken');
            localStorage.removeItem('sessionId');
            localStorage.removeItem('sessionName');
            localStorage.removeItem('sessionPassword');
            delete api.defaults.headers.common['Authorization'];
          }
        }
        
        // No valid authentication found
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
      } catch (err) {
        console.error('Error checking authentication:', err);
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
      }
    };
    
    checkAuthentication();
  }, [sessionId]);

  // Fetch questions when component mounts and is authenticated
  useEffect(() => {
    if (!isAuthenticated || isCheckingAuth) return;
    
    const fetchData = async () => {
      try {
        // First fetch custom columns
        const columnsResponse = await api.get(`/api/custom-columns/session/${sessionId}`);
        const columns = columnsResponse.data;
        setCustomColumns(columns);
        
        // Try to load saved column order from database
        let finalOrder;
        const customColumnIds = columns.map(col => `custom_${col.id}`);
        
        try {
          const orderResponse = await api.get(`/api/column-order/session/${sessionId}`);
          const savedOrder = orderResponse.data.columnOrder;
          
          if (savedOrder && Array.isArray(savedOrder)) {
            // Validate and merge with current custom columns
            // Remove any custom columns that no longer exist
            const validCustomIds = new Set(customColumnIds);
            const filteredOrder = savedOrder.filter(status => {
              if (status.startsWith('custom_')) {
                return validCustomIds.has(status);
              }
              return ['new', 'selected', 'on_air', 'done'].includes(status);
            });
            
            // Add any new custom columns that aren't in saved order
            const existingCustomInOrder = new Set(filteredOrder.filter(s => s.startsWith('custom_')));
            const newCustomColumns = customColumnIds.filter(id => !existingCustomInOrder.has(id));
            
            finalOrder = [...filteredOrder, ...newCustomColumns];
          } else {
            finalOrder = ['new', 'selected', 'on_air', 'done', ...customColumnIds];
          }
        } catch (e) {
          console.error('Error loading saved column order:', e);
          finalOrder = ['new', 'selected', 'on_air', 'done', ...customColumnIds];
        }
        
        setColumnOrder(finalOrder);
        
        // Then fetch questions
        const questionsResponse = await api.get(`/api/sessions/${sessionId}/questions`);
        
        // Group questions by status, including custom columns
        const sortByOrderIndex = (list = []) => {
          return [...list].sort((a, b) => {
            const aHasOrder = typeof a.order_index === 'number';
            const bHasOrder = typeof b.order_index === 'number';

            if (aHasOrder || bHasOrder) {
              if (aHasOrder && !bHasOrder) return -1;
              if (!aHasOrder && bHasOrder) return 1;
              if (aHasOrder && bHasOrder && a.order_index !== b.order_index) {
                return a.order_index - b.order_index;
              }
            }

            const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
            const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
            return dateB - dateA;
          });
        };

        const groupedQuestions = {
          new: [],
          selected: [],
          on_air: [],
          done: []
        };
        
        // Add custom column keys
        columns.forEach(column => {
          groupedQuestions[`custom_${column.id}`] = [];
        });
        
        questionsResponse.data.forEach(question => {
          if (groupedQuestions[question.status] !== undefined) {
            groupedQuestions[question.status].push(question);
          } else {
            // If status doesn't exist, add to 'new'
            groupedQuestions.new.push(question);
          }
        });

        Object.keys(groupedQuestions).forEach(statusKey => {
          groupedQuestions[statusKey] = sortByOrderIndex(groupedQuestions[statusKey]);
        });
        
        setQuestions(groupedQuestions);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchData();
    fetchPolls();
    fetchNameTags();
    fetchNameTagSettings();
    fetchTags();
  }, [sessionId, isAuthenticated, isCheckingAuth]);

  // Save column order to database whenever it changes
  useEffect(() => {
    const saveColumnOrder = async () => {
      if (columnOrder.length > 0 && sessionId) {
        try {
          await api.post(`/api/column-order/session/${sessionId}`, {
            columnOrder
          });
        } catch (error) {
          console.error('Error saving column order:', error);
        }
      }
    };
    
    // Debounce the save to avoid too many API calls
    const timeoutId = setTimeout(saveColumnOrder, 500);
    return () => clearTimeout(timeoutId);
  }, [columnOrder, sessionId]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Set up socket listeners
  useEffect(() => {
    if (socket) {
      console.log('Setting up socket listeners for QuestionManager');
      console.log('Socket ID:', socket.id);
      console.log('Socket connected:', socket.connected);
      
      // Join the session room for real-time updates
      const roomName = `session-${sessionId}`;
      console.log(`Joining room: ${roomName}`);
      socket.emit('join-room', roomName);
      
      // Socket connection events
      socket.on('connect', () => {
        console.log('Socket connected in QuestionManager:', socket.id);
        console.log(`Re-joining room after reconnect: ${roomName}`);
        socket.emit('join-room', roomName);
      });
      
      socket.on('disconnect', (reason) => {
        console.log(`Socket disconnected in QuestionManager: ${reason}`);
      });
      
      socket.on('error', (error) => {
        console.error('Socket error in QuestionManager:', error);
      });
      
      socket.on('room-joined', (data) => {
        console.log(`Successfully joined room: ${data.room} with ${data.clients} client(s)`);
        
        // Send a ping test to verify broadcast functionality
        setTimeout(() => {
          console.log('Sending ping-test to verify broadcast functionality');
          socket.emit('ping-test', { roomName: `session-${sessionId}` });
        }, 1000);
      });
      
      // Test broadcast handler
      socket.on('test-broadcast', (data) => {
        console.log('Received test broadcast:', data);
        console.log(`Test broadcast from ${data.fromSocketId} at ${new Date(data.timestamp).toISOString()}`);
      });
      
      // Pong test handler
      socket.on('pong-test', (data) => {
        console.log('Received pong-test:', data);
        if (data.echo) {
          console.log('This is an echo from the server');
        } else {
          console.log(`Pong from ${data.fromSocketId} at ${new Date(data.timestamp).toISOString()}`);
        }
      });
      
      // New question added
      socket.on('new-question', (question) => {
        console.log('Received new question via socket:', question);
        setQuestions(prev => {
          // Check if the question already exists in ANY column, not just "new"
          // Check with multiple ID formats to handle type mismatches
          const questionExistsInAnyColumn = Object.values(prev).some(columnQuestions => 
            columnQuestions.some(q => 
              q.id === question.id || 
              q.id === String(question.id) || 
              q.id === Number(question.id) ||
              String(q.id) === String(question.id) ||
              Number(q.id) === Number(question.id)
            )
          );
          
          // Check if this question is in our tracking set (recently added manually)
          const isRecentlyAdded = 
            recentlyAddedQuestions.current.has(question.id) ||
            recentlyAddedQuestions.current.has(String(question.id)) ||
            recentlyAddedQuestions.current.has(Number(question.id));
          
          // Only add the question if it doesn't already exist anywhere and wasn't recently added manually
          if (!questionExistsInAnyColumn && !isRecentlyAdded) {
            // Play a sound notification (only for questions from other users)
            try {
              const audio = new Audio('/notification.mp3');
              audio.play().catch(e => console.log('Error playing notification sound', e));
            } catch (e) {
              console.log('Sound notification not supported');
            }
            
            return {
              ...prev,
              new: [...prev.new, question]
            };
          }
          return prev;
        });
      });
      
      // Question status updated
      socket.on('question-status-updated', (data) => {
        console.log('Question status updated via socket:', data);
        const { questionId, status, fromSocketId } = data;
        
        console.log(`Received status update from socket ${fromSocketId}`);
        console.log(`My socket ID: ${socket.id}`);
        
        // Don't update if this client initiated the change (prevents double updates)
        if (recentlyAddedQuestions.current.has(`status-${questionId}`)) {
          console.log('Ignoring status update event as this client initiated it');
          return;
        }
        
        setQuestions(prev => {
          // Create a copy of the current questions
          const newQuestions = { ...prev };
          
          // Find the question and its current status
          let question = null;
          let oldStatus = null;
          
          // Find and remove the question from its current status group
          Object.keys(newQuestions).forEach(statusKey => {
            const found = newQuestions[statusKey].find(q => q.id === questionId);
            if (found) {
              question = { ...found };
              oldStatus = statusKey;
              newQuestions[statusKey] = newQuestions[statusKey].filter(q => q.id !== questionId);
            }
          });
          
          // Update and add the question to its new status group
          if (question) {
            question.status = status;
            
            // Add highlight flag for visual feedback
            question.highlight = true;
            
            // Ensure the new status array exists before pushing
            if (!newQuestions[status]) {
              newQuestions[status] = [];
            }
            
            newQuestions[status].push(question);
            
            console.log(`Question ${questionId} moved from ${oldStatus} to ${status}`);
          }
          
          return newQuestions;
        });
        
        // Remove the highlight after a delay
        setTimeout(() => {
          setQuestions(prev => {
            // Create a deep copy of the current questions
            const newQuestions = JSON.parse(JSON.stringify(prev));
            
            // Find the highlighted question and remove the highlight
            const columnQuestions = newQuestions[status];
            if (columnQuestions) {
              const highlightedQuestion = columnQuestions.find(q => q.id === questionId);
              
              if (highlightedQuestion) {
                highlightedQuestion.highlight = false;
                console.log(`Removed highlight from question ${questionId}`);
              }
            }
            
            // Force a re-render by returning a completely new object
            return { ...newQuestions };
          });
        }, 3000); // Highlight for 3 seconds
      });
      
      // Question content updated
      socket.on('question-updated', (updatedQuestion) => {
        console.log('Question content updated via socket:', updatedQuestion);
        
        setQuestions(prev => {
          // Create a copy of the current questions
          const newQuestions = { ...prev };
          
          // Find and update the question in its current status group
          Object.keys(newQuestions).forEach(status => {
            newQuestions[status] = newQuestions[status].map(q => 
              q.id === updatedQuestion.id ? { ...q, ...updatedQuestion } : q
            );
          });
          
          return newQuestions;
        });
      });
      
      // Question deleted
      socket.on('question-deleted', (questionId) => {
        setQuestions(prev => {
          // Create a copy of the current questions
          const newQuestions = { ...prev };
          
          // Remove the question from all status groups
          Object.keys(newQuestions).forEach(status => {
            newQuestions[status] = newQuestions[status].filter(q => q.id !== questionId);
          });
          
          return newQuestions;
        });
      });
      
      // All questions cleared for a session
      socket.on('questions-cleared', (sessionId) => {
        if (sessionId === session) {
          // Clear all questions for this session
          setQuestions({
            new: [],
            selected: [],
            on_air: [],
            done: []
          });
        }
      });
      
      // Poll status updated
      socket.on('poll-status-updated', (data) => {
        console.log('Poll status updated via socket:', data);
        const { pollId, isOnAir, fromSocketId } = data;
        
        // Don't update if this client initiated the change
        if (fromSocketId === socket.id) {
          console.log('Ignoring poll status update from self');
          return;
        }
        
        console.log('Updating poll status from other client:', { pollId, isOnAir });
        setPolls(prev => prev.map(poll => ({
          ...poll,
          is_on_air: poll.id === pollId ? isOnAir : false
        })));
      });
      
      // Poll shown to audience
      socket.on('poll-shown', (data) => {
        console.log('Poll shown to audience via socket:', data);
        const { poll, fromSocketId } = data;
        
        // Don't update if this client initiated the change
        if (fromSocketId === socket.id) {
          return;
        }
        
        // You can add logic here to show the poll in the display view
        console.log('Poll is now being shown to audience:', poll);
      });
      
      // Poll ask status updated
      socket.on('poll-ask-status-updated', (data) => {
        console.log('Poll ask status updated via socket:', data);
        const { pollId, isAsked, fromSocketId } = data;
        
        // Don't update if this client initiated the change
        if (fromSocketId === socket.id) {
          console.log('Ignoring poll ask status update from self');
          return;
        }
        
        console.log('Updating poll ask status from other client:', { pollId, isAsked });
        setPolls(prev => prev.map(poll => ({
          ...poll,
          is_asked: poll.id === pollId ? isAsked : false
        })));
      });

      // Poll vote updated (when someone votes)
      socket.on('poll-vote-updated', (data) => {
        console.log('Poll vote updated via socket:', data);
        const { pollId, fromSocketId } = data;
        
        // Don't update if this client initiated the change
        if (fromSocketId === socket.id) {
          console.log('Ignoring poll vote update from self');
          return;
        }
        
        console.log('Poll vote updated, refreshing poll data for poll:', pollId);
        
        // Refresh the specific poll data to get updated results
        api.get(`/api/polls/${pollId}/with-results`)
          .then(response => {
            const updatedPoll = response.data;
            console.log('Fetched updated poll with results:', updatedPoll);
            
            setPolls(prev => prev.map(poll => 
              poll.id === pollId ? {
                ...poll,
                // Keep the current displayMode but update results
                results: updatedPoll.results
              } : poll
            ));
          })
          .catch(err => {
            console.error('Error fetching updated poll results:', err);
          });
      });
      
      // Question order updated
      socket.on('questions-reordered', (data) => {
        console.log('Questions reordered via socket:', data);
        const { status, questionIds, sessionId, fromSocketId, eventId, isTest, movedQuestionId } = data;
        
        console.log(`Received reorder event ${eventId} from socket ${fromSocketId}`);
        console.log(`My socket ID: ${socket.id}`);
        console.log('Is test event:', isTest || false);
        console.log('Moved question ID:', movedQuestionId || 'Not specified');
        
        // Check if we have tracking for this status
        const trackingKey = `reorder-${status}`;
        const hasTracking = recentlyAddedQuestions.current.has(trackingKey);
        console.log(`Tracking check for ${trackingKey}:`, hasTracking);
        console.log('All tracking keys:', Array.from(recentlyAddedQuestions.current));
        
        // Don't update if this client initiated the change (prevents double updates)
        if (hasTracking) {
          console.log('✅ Ignoring reorder event as this client initiated it');
          return;
        }
        
        console.log('⚠️ APPLYING reorder event from another client or server');
        
        console.log(`Applying reordered questions for ${status} column`);
        console.log('New order:', questionIds);
        
        // Force a re-render by creating a completely new state object
        setQuestions(prev => {
          if (!prev || !prev[status]) {
            console.warn(`No questions found for status ${status} when applying reorder`);
            return prev;
          }

          // Create a deep copy of the current questions
          const newQuestions = JSON.parse(JSON.stringify(prev));
          
          // Get the current questions in this column
          const currentQuestions = [...newQuestions[status]];
          
          // Log current order before reordering
          console.log('Current order before reordering:', 
            currentQuestions.map(q => q.id.toString())
          );
          
          // Create a new array with questions in the order specified by questionIds
          const reorderedQuestions = [];
          
          // First add questions in the order specified by questionIds
          questionIds.forEach(id => {
            const question = currentQuestions.find(q => q.id.toString() === id);
            if (question) {
              question.order_index = reorderedQuestions.length;
              // If this is the moved question, mark it for highlighting
              if (movedQuestionId && id === movedQuestionId) {
                question.highlight = true;
              }
              reorderedQuestions.push(question);
            } else {
              console.warn(`Question with ID ${id} not found in ${status} column`);
            }
          });
          
          // Then add any questions that weren't in questionIds (shouldn't happen, but just in case)
          currentQuestions.forEach(question => {
            if (!questionIds.includes(question.id.toString())) {
              console.warn(`Question ${question.id} was not in the new order, appending to end`);
              question.order_index = reorderedQuestions.length;
              reorderedQuestions.push(question);
            }
          });
          
          // Update the column with the reordered questions
          newQuestions[status] = reorderedQuestions;
          
          console.log(`Updated ${status} column with reordered questions:`, 
            reorderedQuestions.map(q => q.id.toString())
          );
          
          // Force a re-render by returning a completely new object
          return { ...newQuestions };
        });
        
        // If a question was moved, remove the highlight after a delay
        if (movedQuestionId) {
          setTimeout(() => {
            setQuestions(prev => {
              // Create a deep copy of the current questions
              const newQuestions = JSON.parse(JSON.stringify(prev));
              
              // Find the highlighted question and remove the highlight
              const columnQuestions = newQuestions[status];
              const highlightedQuestion = columnQuestions.find(q => q.id.toString() === movedQuestionId);
              
              if (highlightedQuestion) {
                highlightedQuestion.highlight = false;
                console.log(`Removed highlight from question ${movedQuestionId}`);
              }
              
              // Force a re-render by returning a completely new object
              return { ...newQuestions };
            });
          }, 3000); // Highlight for 3 seconds
        }
        
        // If this is a test event, send a confirmation back
        if (isTest) {
          console.log('Sending test confirmation');
          socket.emit('test-reorder-received', {
            status,
            eventId,
            receivedAt: Date.now(),
            receiverSocketId: socket.id
          });
        }
      });
      
      // Confirmation that our reorder broadcast was sent
      socket.on('reorder-broadcast-confirmed', (data) => {
        console.log('Reorder broadcast confirmed:', data);
        const { status, recipients } = data;
        
        console.log(`Reorder for ${status} column was broadcast to ${recipients} other client(s)`);
        
        // Tracking is now managed by the drag handler's timeout
      });
      
      // QR code display events
      socket.on('show-qr-code', (data) => {
        console.log('QR code shown via socket:', data);
        const { fromSocketId } = data;
        
        // Update state for all clients
        setIsQRCodeShowing(true);
      });
      
      socket.on('hide-qr-code', (data) => {
        console.log('QR code hidden via socket:', data);
        const { fromSocketId } = data;
        
        // Update state for all clients
        setIsQRCodeShowing(false);
      });
      
      // Name tag on-air status updated
      socket.on('name-tag-on-air-updated', (data) => {
        console.log('=== QUESTIONMANAGER: NAME TAG ON-AIR UPDATED ===');
        console.log('Name tag on-air status updated via socket:', data);
        const { sessionId: eventSessionId } = data;
        
        if (String(eventSessionId) === String(sessionId)) {
          console.log('✅ QuestionManager: Fetching updated name tags from API...');
          
          // Fetch updated name tags from API (like polls do)
          api.get(`/api/name-tags/session/${sessionId}`)
            .then(response => {
              const updatedNameTags = response.data;
              console.log('QuestionManager: Fetched updated name tags:', updatedNameTags);
              setNameTags(updatedNameTags);
            })
            .catch(err => {
              console.error('QuestionManager: Error fetching updated name tags:', err);
            });
        }
        console.log('=== END QUESTIONMANAGER: NAME TAG ON-AIR UPDATED ===');
      });

      // Name tag updated (when edited)
      socket.on('name-tag-updated', (data) => {
        console.log('=== QUESTIONMANAGER: NAME TAG UPDATED ===');
        console.log('Name tag updated via socket:', data);
        const { sessionId: eventSessionId } = data;
        
        if (String(eventSessionId) === String(sessionId)) {
          console.log('✅ QuestionManager: Fetching updated name tags from API...');
          
          // Fetch updated name tags from API (like polls do)
          api.get(`/api/name-tags/session/${sessionId}`)
            .then(response => {
              const updatedNameTags = response.data;
              console.log('QuestionManager: Fetched updated name tags after edit:', updatedNameTags);
              setNameTags(updatedNameTags);
            })
            .catch(err => {
              console.error('QuestionManager: Error fetching updated name tags after edit:', err);
            });
        }
        console.log('=== END QUESTIONMANAGER: NAME TAG UPDATED ===');
      });

      // Listen for name tag settings updates
      socket.on('name-tag-settings-updated', (data) => {
        console.log('=== QUESTIONMANAGER: NAME TAG SETTINGS UPDATED ===');
        console.log('Name tag settings updated via socket:', data);
        if (String(data.sessionId) === String(sessionId)) {
          setNameTagSettings(data.settings);
        }
        console.log('=== END QUESTIONMANAGER: NAME TAG SETTINGS UPDATED ===');
      });

      // Listen for QR template updates from other operators
      socket.on('qr-template-updated', (data) => {
        console.log('=== QUESTIONMANAGER: QR TEMPLATE UPDATED ===');
        console.log('QR template updated via socket:', data);
        if (String(data.sessionId) === String(sessionId) && data.template) {
          setQrTemplate(data.template);
          // If the modal is open, it will automatically update via the useEffect
        }
        console.log('=== END QUESTIONMANAGER: QR TEMPLATE UPDATED ===');
      });

      // Listen for flow grid settings updates from other operators
      socket.on('flow-grid-settings-updated', (data) => {
        console.log('=== QUESTIONMANAGER: FLOW GRID SETTINGS UPDATED ===');
        console.log('Flow grid settings updated via socket:', data);
        if (String(data.sessionId) === String(sessionId) && data.settings) {
          // Ensure scrollingEnabled defaults to true if not set
          setFlowGridSettings({
            ...data.settings,
            scrollingEnabled: data.settings.scrollingEnabled !== undefined ? data.settings.scrollingEnabled : true
          });
          // If the modal is open, it will automatically update via the useEffect
        }
        console.log('=== END QUESTIONMANAGER: FLOW GRID SETTINGS UPDATED ===');
      });
      
      // Listen for live text settings updates from other operators
      socket.on('live-text-settings-updated', (data) => {
        console.log('=== QUESTIONMANAGER: LIVE TEXT SETTINGS UPDATED ===');
        console.log('Live text settings updated via socket:', data);
        if (String(data.sessionId) === String(sessionId) && data.settings) {
          const updatedSettings = normalizeLiveTextSettings(data.settings);
          setLiveTextSettings(updatedSettings);
          setLiveTextInput(updatedSettings.text);
          // If the modal is open, it will automatically update via the useEffect
        }
        console.log('=== END QUESTIONMANAGER: LIVE TEXT SETTINGS UPDATED ===');
      });

      // Enhanced debug logging for real-time updates
      console.log('Socket room:', `session-${sessionId}`);
      
      return () => {
        console.log('Cleaning up socket listeners');
        socket.off('new-question');
        socket.off('question-status-updated');
        socket.off('question-updated');
        socket.off('question-deleted');
        socket.off('questions-cleared');
        socket.off('questions-reordered');
        socket.off('reorder-broadcast-confirmed');
        socket.off('test-broadcast');
        socket.off('pong-test');
        socket.off('poll-status-updated');
        socket.off('poll-shown');
        socket.off('poll-ask-status-updated');
        socket.off('poll-vote-updated');
        socket.off('name-tag-on-air-updated');
        socket.off('name-tag-updated');
        socket.off('name-tag-settings-updated');
        socket.off('qr-template-updated');
        socket.off('flow-grid-settings-updated');
        socket.off('live-text-settings-updated');
        socket.off('show-qr-code');
        socket.off('hide-qr-code');
      };
    }
  }, [socket]);


  const handleAddQuestion = async (e) => {
    e.preventDefault();
    
    if (!newQuestion.trim()) {
      setError('Question content cannot be empty');
      return;
    }
    
    try {
      const response = await api.post(`/api/sessions/${sessionId}/questions`, {
        content: newQuestion,
        sessionId
      });
      
      // Track this question ID immediately to prevent duplicate from socket event
      // Use both string and number in case of type mismatch
      const questionId = response.data.id;
      recentlyAddedQuestions.current.add(questionId);
      recentlyAddedQuestions.current.add(String(questionId));
      recentlyAddedQuestions.current.add(Number(questionId));
      
      // Check if question already exists in state before adding
      setQuestions(prev => {
        const questionExists = Object.values(prev).some(columnQuestions => 
          columnQuestions.some(q => q.id === questionId || q.id === String(questionId) || q.id === Number(questionId))
        );
        
        if (!questionExists) {
          return {
            ...prev,
            new: [...prev.new, response.data]
          };
        }
        return prev;
      });
      
      // Clean up the tracking after a delay (socket event should arrive quickly)
      setTimeout(() => {
        recentlyAddedQuestions.current.delete(questionId);
        recentlyAddedQuestions.current.delete(String(questionId));
        recentlyAddedQuestions.current.delete(Number(questionId));
      }, 2000);
      
      // Clear the input
      setNewQuestion('');
      setError(null);
    } catch (err) {
      console.error('Error adding question:', err);
      setError('Failed to add question. Please try again.');
    }
  };

  // Handle dropdown actions
  const handleAddColumn = () => {
    setShowAddDropdown(false);
    setShowCustomColumnModal(true);
  };

  const handleAddQuestionFromDropdown = () => {
    setShowAddDropdown(false);
    setShowAddQuestionModal(true);
  };

  // Handle main dropdown actions
  const handleMainAddColumn = () => {
    setShowMainAddDropdown(false);
    setShowCustomColumnModal(true);
  };

  const handleMainAddQuestion = () => {
    setShowMainAddDropdown(false);
    setShowAddQuestionModal(true);
  };

  // Handle adding question from modal
  const handleAddQuestionFromModal = async (questionData) => {
    try {
      const response = await api.post(`/api/sessions/${sessionId}/questions`, {
        content: questionData.content,
        submitterName: questionData.submitter_name,
        sessionId
      });
      
      // Track this question ID immediately to prevent duplicate from socket event
      // Use both string and number in case of type mismatch
      const questionId = response.data.id;
      recentlyAddedQuestions.current.add(questionId);
      recentlyAddedQuestions.current.add(String(questionId));
      recentlyAddedQuestions.current.add(Number(questionId));
      
      // Check if question already exists in state before adding
      setQuestions(prev => {
        const questionExists = Object.values(prev).some(columnQuestions => 
          columnQuestions.some(q => q.id === questionId || q.id === String(questionId) || q.id === Number(questionId))
        );
        
        if (!questionExists) {
          return {
            ...prev,
            new: [...prev.new, response.data]
          };
        }
        return prev;
      });
      
      // Clean up the tracking after a delay (socket event should arrive quickly)
      setTimeout(() => {
        recentlyAddedQuestions.current.delete(questionId);
        recentlyAddedQuestions.current.delete(String(questionId));
        recentlyAddedQuestions.current.delete(Number(questionId));
      }, 2000);
    } catch (err) {
      console.error('Error adding question:', err);
      setError('Failed to add question. Please try again.');
    }
  };

  // Handle toggling QR code display
  const handleToggleQR = () => {
    if (socket) {
      if (isQRCodeShowing) {
        // Hide QR code
        socket.emit('hide-qr-code', { sessionId });
        setIsQRCodeShowing(false);
        setSuccess('QR code hidden');
        
        // Track event
        if (window.umami) {
          window.umami.track('Hide QR Code');
        }
      } else {
        // Show QR code
        socket.emit('show-qr-code', { sessionId });
        setIsQRCodeShowing(true);
        setSuccess('QR code is now displayed');
        
        // Track event
        if (window.umami) {
          window.umami.track('Show QR Code');
        }
      }
    }
  };

  // Handle toggling QR template display mode
  const handleToggleQRTemplate = async () => {
    if (!qrTemplate) return;
    
    try {
      const newDisplayMode = qrTemplate.display_mode === 'fullscreen' ? 'lower-third' : 'fullscreen';
      
      const response = await api.put(`/api/qr-templates/session/${sessionId}`, {
        ...normalizeQrTemplate(qrTemplate),
        display_mode: newDisplayMode
      });
      
      setQrTemplate(normalizeQrTemplate(response.data));
      setSuccess(`QR template switched to ${newDisplayMode === 'fullscreen' ? 'Full Screen' : 'Lower Third'} mode`);
      
      // Emit socket event to notify DisplayView of template change
      if (socket) {
        console.log('Emitting qr-template-updated event:', {
          sessionId,
          template: response.data
        });
        socket.emit('qr-template-updated', {
          sessionId,
          template: response.data
        });
      } else {
        console.log('Socket not available, cannot emit qr-template-updated event');
      }
    } catch (error) {
      console.error('Error updating QR template:', error);
      alert('Failed to update QR template');
    }
  };

  // Handle QR settings update
  const handleQRSettingsUpdate = async (settings) => {
    try {
      const response = await api.put(`/api/qr-templates/session/${sessionId}`, normalizeQrTemplate(settings));
      setQrTemplate(normalizeQrTemplate(response.data));
      setSuccess('QR code settings updated successfully');
      
      // Emit socket event to update display views
      if (socket) {
        socket.emit('qr-template-updated', {
          sessionId,
          template: response.data
        });
      }
      
      // Track event
      if (window.umami) {
        window.umami.track('QR Template Updated');
      }
    } catch (error) {
      console.error('Error updating QR settings:', error);
      setError('Failed to update QR code settings');
    }
  };

  // Handle QR background image upload
  const handleQRImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image file must be smaller than 5MB.');
      return;
    }

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('backgroundImage', file);

      // Upload file to server
      const response = await fetch('/api/upload/qr-background', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Update QR template with the file URL
        const updatedSettings = setQrModeValues(
          normalizeQrTemplate(qrTemplate || DEFAULT_QR_TEMPLATE),
          'fullscreen',
          { background_image: result.fileUrl }
        );
        await handleQRSettingsUpdate(updatedSettings);
      } else {
        alert('Error uploading image: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading QR background image:', error);
      alert('Error uploading image. Please try again.');
    }
  };

  // Handle toggling reactions enabled
  const handleToggleReactions = async () => {
    try {
      const newReactionsEnabled = !reactionsEnabled;
      
      const response = await api.put(`/api/sessions/${sessionId}`, {
        reactions_enabled: newReactionsEnabled
      });
      
      setReactionsEnabled(newReactionsEnabled);
      setSuccess(`Reactions ${newReactionsEnabled ? 'enabled' : 'disabled'}`);
      
      // Emit socket event to notify AudienceView of reactions change
      if (socket) {
        socket.emit('reactions-settings-updated', {
          sessionId,
          reactionsEnabled: newReactionsEnabled,
          reactionIcons: reactionIcons
        });
      }
    } catch (error) {
      console.error('Error updating reactions settings:', error);
      alert('Failed to update reactions settings');
    }
  };

  // Handle updating reaction icons
  const handleUpdateReactionIcons = async (newIcons) => {
    try {
      const response = await api.put(`/api/sessions/${sessionId}`, {
        reaction_icons: JSON.stringify(newIcons)
      });
      
      setReactionIcons(newIcons);
      setSuccess('Reaction icons updated');
      
      // Emit socket event to notify AudienceView of reactions change
      if (socket) {
        socket.emit('reactions-settings-updated', {
          sessionId,
          reactionsEnabled: reactionsEnabled,
          reactionIcons: newIcons
        });
      }
    } catch (error) {
      console.error('Error updating reaction icons:', error);
      alert('Failed to update reaction icons');
    }
  };

  // Handle updating flow grid settings
  const handleFlowGridSettingsUpdate = async (key, value) => {
    try {
      const updatedSettings = { ...flowGridSettings, [key]: value };
      setFlowGridSettings(updatedSettings);
      
      const response = await api.put(`/api/sessions/${sessionId}/flow-grid-settings`, updatedSettings);
      
      if (response.data.success) {
        // Emit socket event to notify FlowGridView of settings change
        if (socket) {
          socket.emit('flow-grid-settings-updated', {
            sessionId,
            settings: updatedSettings
          });
        }
      }
    } catch (error) {
      console.error('Error updating flow grid settings:', error);
      alert('Failed to update flow grid settings');
    }
  };

  // Handle flow grid settings update from modal (batch update)
  const handleFlowGridSettingsModalUpdate = async (settings) => {
    try {
      // Merge with current settings to preserve sourceColumn
      const updatedSettings = { ...flowGridSettings, ...settings };
      setFlowGridSettings(updatedSettings);
      
      const response = await api.put(`/api/sessions/${sessionId}/flow-grid-settings`, updatedSettings);
      
      if (response.data.success) {
        // Emit socket event to notify FlowGridView of settings change
        if (socket) {
          socket.emit('flow-grid-settings-updated', {
            settings: updatedSettings,
            sessionId: sessionId
          });
        }
        
        setSuccess('Flow grid settings updated successfully');
      }
    } catch (error) {
      console.error('Error updating flow grid settings:', error);
      setError('Failed to update flow grid settings');
    }
  };

  // Handle live text settings update from modal (only colors, font size, margin)
  const handleLiveTextSettingsModalUpdate = async (settings) => {
    try {
      // Keep the current text, only update styling settings
      const updatedSettings = { ...liveTextSettings, ...settings };
      setLiveTextSettings(updatedSettings);
      
      const response = await api.put(`/api/sessions/${sessionId}/live-text-settings`, updatedSettings);
      
      if (response.data.success) {
        // Emit socket event to notify LiveTextView of settings change
        if (socket) {
          socket.emit('live-text-settings-updated', {
            settings: updatedSettings,
            sessionId: sessionId
          });
        }
        
        setSuccess('Live text settings updated successfully');
      }
    } catch (error) {
      console.error('Error updating live text settings:', error);
      setError('Failed to update live text settings');
    }
  };

  // Handle sending live text (update text only). Empty / whitespace-only is allowed and clears the display.
  const handleSendLiveText = async () => {
    try {
      const updatedSettings = { ...liveTextSettings, text: liveTextInput ?? '' };
      setLiveTextSettings(updatedSettings);
      
      const response = await api.put(`/api/sessions/${sessionId}/live-text-settings`, updatedSettings);
      
      if (response.data.success) {
        // Emit socket event to notify LiveTextView of text change
        if (socket) {
          socket.emit('live-text-settings-updated', {
            settings: updatedSettings,
            sessionId: sessionId
          });
        }
        
        setSuccess(hasLiveTextContent(updatedSettings.text) ? 'Live text sent successfully' : 'Live text cleared');
      }
    } catch (error) {
      console.error('Error sending live text:', error);
      setError('Failed to send live text');
    }
  };

  // Handle image upload for flow grid background
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image file must be smaller than 5MB.');
      return;
    }

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('backgroundImage', file);

      // Upload file to server
      const response = await fetch('/api/upload/flow-grid-background', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Update flow grid settings with the file URL
        handleFlowGridSettingsUpdate('backgroundImage', result.fileUrl);
      } else {
        alert('Error uploading image: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    }
  };

  // Handle putting a question on air
  const handlePutOnAir = async (questionId) => {
    try {
      // First, move any question that's currently on air to done
      const allQuestions = Object.values(questions).flat();
      const currentOnAirQuestions = allQuestions.filter(q => q.status === 'on_air');
      for (const onAirQuestion of currentOnAirQuestions) {
        await api.put(`/api/questions/${onAirQuestion.id}/status`, {
          status: 'done', // Move to done instead of selected
          sessionId
        });
      }

      // Then put the new question on air
      await api.put(`/api/questions/${questionId}/status`, {
        status: 'on_air',
        sessionId
      });
    } catch (error) {
      console.error('Error putting question on air:', error);
      setError('Failed to put question on air');
    }
  };

  const handleStatusChange = async (questionId, newStatus) => {
    try {
      console.log(`Updating question ${questionId} to status ${newStatus}`);
      console.log(`Using session ID: ${sessionId}`);
      
      // Check if trying to set a question to "on_air" when another is already on air
      if (newStatus === 'on_air' && questions.on_air.length > 0) {
        const existingOnAir = questions.on_air[0];
        if (existingOnAir.id !== questionId) {
          setError('Only one question can be on air at a time. Please remove the current on-air question first.');
          return;
        }
      }
      
      // Add to recently updated to prevent duplicate updates
      recentlyAddedQuestions.current.add(`status-${questionId}`);
      
      // First update using the API
      const response = await api.put(`/api/questions/${questionId}/status`, {
        status: newStatus,
        sessionId
      });
      
      console.log('Server response:', response.data);
      
      // Then emit the update via socket for real-time sync across clients
      if (socket) {
        socket.emit('update-question-status', {
          questionId: parseInt(questionId),
          status: newStatus,
          sessionId,
          fromSocketId: socket.id
        });
        console.log('Emitted status update event via socket');
      }
      
      // Remove from tracking after a delay to prevent socket handler from processing
      setTimeout(() => {
        recentlyAddedQuestions.current.delete(`status-${questionId}`);
      }, 1000);
      
      // Update the question in the state
      setQuestions(prev => {
        // Find the question
        let questionToUpdate = null;
        let currentStatus = null;
        
        // Check all possible status groups including custom columns
        const allStatusGroups = [
          'new', 'selected', 'on_air', 'done',
          ...customColumns.map(col => `custom_${col.id}`)
        ];
        
        for (const status of allStatusGroups) {
          const found = prev[status]?.find(q => q.id === questionId);
          if (found) {
            questionToUpdate = found;
            currentStatus = status;
            break;
          }
        }
        
        if (!questionToUpdate) {
          console.warn(`Question ${questionId} not found in any status`);
          console.log('Available status groups:', Object.keys(prev));
          console.log('All status groups checked:', allStatusGroups);
          
          // Try to find the question in any status group (fallback)
          for (const status of allStatusGroups) {
            const found = prev[status]?.find(q => q.id.toString() === questionId.toString());
            if (found) {
              console.log(`Found question ${questionId} in status ${status} as fallback`);
              questionToUpdate = found;
              currentStatus = status;
              break;
            }
          }
          
          if (!questionToUpdate) {
            console.error(`Question ${questionId} not found anywhere, skipping update`);
            return prev;
          }
        }
        
        // If already in the correct status, just update the data
        if (currentStatus === newStatus) {
          console.log(`Question ${questionId} already in status ${newStatus}, updating data only`);
          const newQuestions = { ...prev };
          newQuestions[currentStatus] = prev[currentStatus].map(q => 
            q.id === questionId ? { ...q, ...response.data, status: newStatus } : q
          );
          return newQuestions;
        }
        
        // Create a completely new state object with new array references
        const newQuestions = {};
        
        // Ensure the destination status group exists
        if (!prev[newStatus]) {
          newQuestions[newStatus] = [];
        }
        
        // Copy all status arrays, creating new arrays for changed statuses
        Object.keys(prev).forEach(status => {
          if (status === currentStatus) {
            // Remove from current status - create new array
            newQuestions[status] = prev[status].filter(q => q.id !== questionId);
          } else if (status === newStatus) {
            // Add to new status - create new array
            newQuestions[status] = [
              ...(prev[status] || []),
              {
                ...questionToUpdate,
                ...response.data,
                status: newStatus
              }
            ];
          } else {
            // Copy other statuses as-is
            newQuestions[status] = prev[status];
          }
        });
        
        console.log(`Updated state: moved question ${questionId} from ${currentStatus} to ${newStatus}`);
        return newQuestions;
      });
    } catch (err) {
      console.error('Error updating question status:', err);
      setError('Failed to update question status. Please try again.');
    }
  };
  
  // Handle editing a question
  const handleEditQuestion = (question) => {
    // First set the content, then show the modal
    setEditContent(question.content);
    setEditingQuestion(question);
    
    // Use a small timeout to ensure state is updated before showing the modal
    setTimeout(() => {
      setShowEditModal(true);
    }, 0);
  };
  
  // Save edited question
  const handleSaveEdit = async () => {
    if (!editContent.trim()) {
      setEditError('Question content cannot be empty');
      return;
    }
    
    // Store the values we need before closing the modal
    const questionId = editingQuestion.id;
    const newContent = editContent;
    
    // Close the modal immediately for better UX
    setShowEditModal(false);
    
    try {
      const response = await api.put(`/api/questions/${questionId}/content`, {
        content: newContent,
        sessionId
      });
      
      // Get the updated question from the response
      const updatedQuestion = response.data;
      
      // Update the question in the state
      setQuestions(prev => {
        const newQuestions = { ...prev };
        
        // Find which status group contains the question
        Object.keys(newQuestions).forEach(status => {
          const index = newQuestions[status].findIndex(q => q.id === questionId);
          if (index !== -1) {
            newQuestions[status][index] = {
              ...newQuestions[status][index],
              content: newContent
            };
          }
        });
        
        return newQuestions;
      });
      
      // Emit socket event to update DisplayView
      if (socket) {
        socket.emit('question-updated', {
          id: questionId,
          content: newContent,
          sessionId
        });
        
        console.log('Emitted question update event for DisplayView');
      }
      
      // Clear the editing state
      setEditingQuestion(null);
      setEditContent('');
      setEditError(null);
    } catch (err) {
      console.error('Error updating question:', err);
      // Show error notification instead of modal error since modal is closed
      setError('Failed to update question. Please try again.');
    }
  };
  
  // Handle deleting a question
  const handleDeleteQuestion = async (questionId) => {
    try {
      await api.delete(`/api/questions/${questionId}`, {
        data: { sessionId }
      });
      
      // Remove the question from the state
      setQuestions(prev => {
        const newQuestions = { ...prev };
        
        // Find which status group contains the question
        Object.keys(newQuestions).forEach(status => {
          newQuestions[status] = newQuestions[status].filter(q => q.id !== questionId);
        });
        
        return newQuestions;
      });
    } catch (err) {
      console.error('Error deleting question:', err);
      setError('Failed to delete question. Please try again.');
    }
  };

  // Poll management functions
  const handleCreatePoll = async (pollData) => {
    try {
      const response = await api.post(`/api/polls/session/${sessionId}`, pollData);
      setPolls(prev => [response.data, ...prev]);
      setShowPollModal(false);
    } catch (error) {
      console.error('Error creating poll:', error);
      setError('Failed to create poll. Please try again.');
    }
  };

  const handleEditPoll = async (pollData) => {
    try {
      await api.put(`/api/polls/${editingPoll.id}`, pollData);
      setPolls(prev => prev.map(poll => 
        poll.id === editingPoll.id ? { ...poll, ...pollData } : poll
      ));
      setShowPollModal(false);
      setEditingPoll(null);
    } catch (error) {
      console.error('Error updating poll:', error);
      setError('Failed to update poll. Please try again.');
    }
  };

  const handleDeletePoll = async (pollId) => {
    try {
      await api.delete(`/api/polls/${pollId}`);
      setPolls(prev => prev.filter(poll => poll.id !== pollId));
    } catch (error) {
      console.error('Error deleting poll:', error);
      setError('Failed to delete poll. Please try again.');
    }
  };

  const handleTogglePollOnAir = async (pollId, isOnAir) => {
    console.log('handleTogglePollOnAir called:', { pollId, isOnAir, sessionId });
    console.log('Socket status:', { 
      hasSocket: !!socket, 
      connected: socket?.connected, 
      socketId: socket?.id 
    });
    
    try {
      console.log('Making API call to update poll status...');
      const response = await api.put(`/api/polls/${pollId}/on-air`, { is_on_air: isOnAir });
      console.log('API response:', response.data);
      
      // Find the poll to get its current display mode
      const poll = polls.find(p => p.id === pollId);
      const displayMode = poll ? poll.displayMode : 'question';
      
      setPolls(prev => prev.map(poll => ({
        ...poll,
        is_on_air: poll.id === pollId ? isOnAir : false
      })));
      
      // Note: Socket events are handled by the backend now
      // Backend emits 'poll-status-updated' which DisplayView listens to
      console.log('Poll status updated successfully, backend will notify other clients');
    } catch (error) {
      console.error('Error updating poll status:', error);
      setError('Failed to update poll status. Please try again.');
    }
  };

  const handleAskPoll = async (pollId, isAsked) => {
    console.log('handleAskPoll called:', { pollId, isAsked, sessionId });
    console.log('Socket status:', { 
      hasSocket: !!socket, 
      connected: socket?.connected, 
      socketId: socket?.id 
    });
    
    try {
      console.log('Making API call to update poll ask status...');
      const response = await api.put(`/api/polls/${pollId}/ask`, { is_asked: isAsked });
      console.log('API response:', response.data);
      
      setPolls(prev => prev.map(poll => ({
        ...poll,
        is_asked: poll.id === pollId ? isAsked : false
      })));
      
      // Note: Socket events are handled by the backend now
      // Backend emits 'poll-ask-updated' which AudienceView listens to
      console.log('Poll ask status updated successfully, backend will notify other clients');
    } catch (error) {
      console.error('Error updating poll ask status:', error);
      setError('Failed to update poll ask status. Please try again.');
    }
  };

  const handleResetPollResults = async (pollId) => {
    try {
      console.log('Resetting poll results for poll:', pollId);
      const response = await api.delete(`/api/polls/${pollId}/results`);
      console.log('Reset results response:', response.data);
      
      // Refresh polls to get updated data
      await fetchPolls();
      
      setSuccess('Poll results have been reset successfully');
    } catch (error) {
      console.error('Error resetting poll results:', error);
      setError('Failed to reset poll results. Please try again.');
    }
  };

  const handleTogglePollDisplayMode = async (pollId) => {
    const poll = polls.find(p => p.id === pollId);
    if (!poll) return;
    
    const newDisplayMode = poll.displayMode === 'question' ? 'results' : 'question';
    
    console.log('Toggling poll display mode:', { pollId, newDisplayMode, sessionId });
    
    try {
      // Update the display mode in the database
      await api.put(`/api/polls/${pollId}/display-mode`, { display_mode: newDisplayMode });
      
      // Update the local state
      setPolls(prev => prev.map(poll => {
        if (poll.id === pollId) {
          return {
            ...poll,
            displayMode: newDisplayMode
          };
        }
        return poll;
      }));
      
      // Emit socket event to notify DisplayView
      if (socket) {
        console.log('Emitting poll-display-mode-updated socket event...');
        socket.emit('poll-display-mode-updated', {
          pollId,
          displayMode: newDisplayMode,
          sessionId
        });
        console.log('Socket event emitted successfully');
      } else {
        console.log('No socket available for emitting event');
      }
    } catch (error) {
      console.error('Error updating poll display mode:', error);
      setError('Failed to update poll display mode. Please try again.');
    }
  };

  const handlePollModalSubmit = (pollData) => {
    if (editingPoll) {
      handleEditPoll(pollData);
    } else {
      handleCreatePoll(pollData);
    }
  };

  // Name Tag management functions
  const handleCreateNameTag = async (nameTagData) => {
    try {
      const response = await api.post(`/api/name-tags/session/${sessionId}`, nameTagData);
      setNameTags(prev => [response.data, ...prev]);
      setSuccess('Name tag created successfully');
    } catch (error) {
      console.error('Error creating name tag:', error);
      setError('Failed to create name tag');
    }
  };

  const handleEditNameTag = async (nameTagData) => {
    try {
      const response = await api.put(`/api/name-tags/${nameTagData.id}`, nameTagData);
      setNameTags(prev => prev.map(tag => tag.id === response.data.id ? response.data : tag));
      setSuccess('Name tag updated successfully');
    } catch (error) {
      console.error('Error updating name tag:', error);
      setError('Failed to update name tag');
    }
  };

  const handleDeleteNameTag = async (nameTagId) => {
    try {
      await api.delete(`/api/name-tags/${nameTagId}`);
      setNameTags(prev => prev.filter(tag => tag.id !== nameTagId));
      setSuccess('Name tag deleted successfully');
    } catch (error) {
      console.error('Error deleting name tag:', error);
      setError('Failed to delete name tag');
    }
  };

  const handleCloneNameTag = async (nameTag) => {
    try {
      // Create a copy of the name tag with "(Copy)" appended to the name
      const clonedData = {
        name: `${nameTag.name} (Copy)`,
        subtitle: nameTag.subtitle,
        placement: nameTag.placement,
        background_color: nameTag.background_color,
        name_color: nameTag.name_color,
        subtitle_color: nameTag.subtitle_color
      };
      
      const response = await api.post(`/api/name-tags/session/${sessionId}`, clonedData);
      setNameTags(prev => [response.data, ...prev]);
      setSuccess('Name tag cloned successfully');
    } catch (error) {
      console.error('Error cloning name tag:', error);
      setError('Failed to clone name tag');
    }
  };

  const handleToggleNameTagOnAir = async (nameTagId, isOnAir) => {
    try {
      const response = await api.put(`/api/name-tags/${nameTagId}/on-air`, { isOnAir });
      setNameTags(prev => prev.map(tag => 
        tag.id === nameTagId ? { ...tag, is_on_air: isOnAir } : tag
      ));
      
      // Emit socket event to notify DisplayView
      if (socket) {
        socket.emit('name-tag-on-air-updated', {
          nameTagId,
          isOnAir,
          sessionId
        });
      }
    } catch (error) {
      console.error('Error updating name tag on air status:', error);
      setError('Failed to update name tag status');
    }
  };

  const handleNameTagModalSubmit = (nameTagData) => {
    if (editingNameTag) {
      handleEditNameTag(nameTagData);
    } else {
      handleCreateNameTag(nameTagData);
    }
  };

  const handleUpdateNameTagPlacement = async (nameTagId, newPlacement) => {
    try {
      console.log('=== UPDATING NAME TAG PLACEMENT ===');
      console.log('Name tag ID:', nameTagId);
      console.log('New placement:', newPlacement);
      
      // Find the current name tag to get all its data
      const currentNameTag = nameTags.find(tag => tag.id === nameTagId);
      if (!currentNameTag) {
        setError('Name tag not found');
        return;
      }

      // Update with all current data plus new placement
      const updateData = {
        name: currentNameTag.name,
        subtitle: currentNameTag.subtitle,
        placement: newPlacement,
        background_color: currentNameTag.background_color,
        name_color: currentNameTag.name_color,
        subtitle_color: currentNameTag.subtitle_color
      };

      console.log('Update data:', updateData);
      const response = await api.put(`/api/name-tags/${nameTagId}`, updateData);
      console.log('API response:', response.data);
      
      setNameTags(prev => prev.map(tag => 
        tag.id === nameTagId ? { ...tag, placement: newPlacement } : tag
      ));
      
      // Note: Backend should emit socket event, no need for frontend emission
      console.log('Position change completed, backend should emit socket event');
      
      setSuccess('Name tag position updated successfully');
      console.log('=== END UPDATING NAME TAG PLACEMENT ===');
    } catch (error) {
      console.error('Error updating name tag placement:', error);
      setError('Failed to update name tag position');
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    const [status, questionId] = active.id.split('-');
    
    console.log('Drag started:', { activeId: active.id, status, questionId });
    
    // Find the question being dragged - search in all questions
    let question;
    const allQuestions = Object.values(questions).flat();
    question = allQuestions.find(q => q.id.toString() === questionId);
    
    const cardElement = document.getElementById(`question-card-${status}-${questionId}`);
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      setDragPreviewSize({ width: rect.width, height: rect.height });
    } else {
      setDragPreviewSize({ width: null, height: null });
    }

    setActiveId(active.id);
    setActiveQuestion(question);
    setActiveStatus(status);
    setIsHoveringOverDroppable(false); // Start with no hover effect
    
    console.log('ActiveId set to:', active.id);
  };

  // Handle drag over
  const handleDragOver = (event) => {
    const { active, over } = event;
    
    if (!over) {
      // Not hovering over any droppable area
      setIsHoveringOverDroppable(false);
      return;
    }
    
    const [sourceStatus] = active.id.split('-');
    
    // Check if we're over a column container, column drop target, or an item
    if (over.id.startsWith('container-')) {
      const destinationStatus = over.id.split('-')[1];
      
      // If dragging to a different column
      if (sourceStatus !== destinationStatus) {
        // Check if destination is "on_air" and already has items
        if (destinationStatus === 'on_air' && questions.on_air.length > 0) {
          // Don't allow more than one item in "on_air"
          return;
        }
        
        setActiveStatus(destinationStatus);
        setIsHoveringOverDroppable(true);
      } else {
        // Same column - don't show scaling effect for reordering within same column
        setIsHoveringOverDroppable(false);
      }
    } else if (over.id.startsWith('column-')) {
      // Handle dropping on column in inbox sidebar
      const destinationStatus = over.id.split('-')[1];
      
      // Update activeStatus whenever hovering over a different column
      if (destinationStatus !== activeStatus) {
        // Check if destination is "on_air" and already has items
        if (destinationStatus === 'on_air' && questions.on_air.length > 0) {
          // Don't allow more than one item in "on_air"
          return;
        }
        
        setActiveStatus(destinationStatus);
      }
      setIsHoveringOverDroppable(true);
    } else if (over.id.startsWith('sortable-column-')) {
      // Handle dropping questions onto sortable columns in inbox sidebar
      const columnId = over.id.split('-')[2];
      
      // Update activeStatus whenever hovering over a different column
      if (columnId !== activeStatus) {
        // Check if destination is "on_air" and already has items
        if (columnId === 'on_air' && questions.on_air.length > 0) {
          // Don't allow more than one item in "on_air"
          return;
        }
        
        setActiveStatus(columnId);
      }
      setIsHoveringOverDroppable(true);
    } else {
      const [destinationStatus] = over.id.split('-');
      
      // Update activeStatus whenever hovering over a different column
      if (destinationStatus !== activeStatus) {
        // Check if destination is "on_air" and already has items
        if (destinationStatus === 'on_air' && questions.on_air.length > 0) {
          // Don't allow more than one item in "on_air"
          return;
        }
        
        setActiveStatus(destinationStatus);
        setIsHoveringOverDroppable(true);
      } else {
        // Same column - don't show scaling effect for reordering within same column
        setIsHoveringOverDroppable(false);
      }
    }
  };

  // Handle column reordering in inbox
  const handleColumnDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      return;
    }
    
    if (active.id.startsWith('sortable-column-') && over.id.startsWith('sortable-column-')) {
      const activeColumnId = active.id.split('-')[2];
      const overColumnId = over.id.split('-')[2];
      
      if (activeColumnId !== overColumnId) {
        // The IDs are already in the correct format for columnOrder
        const activeIndex = columnOrder.findIndex(id => id === activeColumnId);
        const overIndex = columnOrder.findIndex(id => id === overColumnId);
        
        if (activeIndex !== -1 && overIndex !== -1) {
          const newColumnOrder = arrayMove(columnOrder, activeIndex, overIndex);
          setColumnOrder(newColumnOrder);
          
          // Save to database
          saveColumnOrder(newColumnOrder);
        }
      }
    }
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      setActiveQuestion(null);
      setActiveStatus(null);
      return;
    }
    
    // Handle column reordering separately
    if (active.id.startsWith('sortable-column-')) {
      handleColumnDragEnd(event);
      return;
    }
    
    const [sourceStatus, questionId] = active.id.split('-');
    
    // Check if we're over a column container, column drop target, or an item
    let destinationStatus;
    if (over.id.startsWith('container-')) {
      destinationStatus = over.id.split('-')[1];
    } else if (over.id.startsWith('column-')) {
      // Handle dropping on column in inbox sidebar
      destinationStatus = over.id.split('-')[1];
    } else if (over.id.startsWith('sortable-column-')) {
      // Handle dropping questions onto sortable columns in inbox sidebar
      const columnId = over.id.split('-')[2];
      destinationStatus = columnId;
    } else {
      [destinationStatus] = over.id.split('-');
    }
    
    // If dropped in a different column
    if (sourceStatus !== destinationStatus) {
      // Check if destination is "on_air" and already has items
      if (destinationStatus === 'on_air' && questions.on_air.length > 0 && 
          !questions.on_air.some(q => q.id.toString() === questionId)) {
        // Don't allow more than one item in "on_air"
        const existingOnAir = questions.on_air[0];
        setError(`Only one question can be on air at a time. Current on-air: "${existingOnAir.content.substring(0, 50)}..."`);
        setActiveId(null);
        setActiveQuestion(null);
        setActiveStatus(null);
        return;
      }
      
      // Find the question being moved
      const questionToMove = questions[sourceStatus]?.find(q => q.id.toString() === questionId);
      
      if (questionToMove) {
        // Update UI immediately for smooth transition
        setQuestions(prev => {
          // Ensure the source status exists
          if (!prev[sourceStatus]) {
            console.warn(`Source status ${sourceStatus} not found`);
            return prev;
          }
          
          // Create new state with updated arrays
          const newQuestions = { ...prev };
          
          // Remove from source
          newQuestions[sourceStatus] = prev[sourceStatus].filter(q => q.id.toString() !== questionId);
          
          // Ensure destination status array exists
          if (!newQuestions[destinationStatus]) {
            newQuestions[destinationStatus] = [];
          }
          
          // Add to destination with updated status
          newQuestions[destinationStatus] = [
            ...newQuestions[destinationStatus],
            { ...questionToMove, status: destinationStatus }
          ];
          
          return newQuestions;
        });
        
        // Then update the server
        handleStatusChange(questionId, destinationStatus);
        
        // Log the action for debugging
        console.log(`Moved question ${questionId} from ${sourceStatus} to ${destinationStatus}`);
        console.log(`Session name being used: ${session?.name}`);
      }
    } else {
      // Reorder within the same column
      const sourceIndex = questions[sourceStatus].findIndex(q => q.id.toString() === questionId);
      
      let destinationIndex;
      if (over.id.startsWith('container-')) {
        // If dropped on the container, move to the end
        destinationIndex = questions[sourceStatus].length - 1;
      } else {
        destinationIndex = questions[sourceStatus].findIndex(q => 
          q.id.toString() === over.id.split('-')[1]
        );
      }
      
      if (sourceIndex !== destinationIndex && sourceIndex !== -1 && destinationIndex !== -1) {
        console.log(`Reordering in same column: ${sourceStatus}, from ${sourceIndex} to ${destinationIndex}`);
        
        // Add to tracking to prevent duplicate updates from socket
        const reorderKey = `reorder-${sourceStatus}`;
        recentlyAddedQuestions.current.add(reorderKey);
        
        // Store the new order for socket emission
        let newQuestionIds = null;
        
        // Update the UI immediately
        setQuestions(prev => {
          console.log('State update - Previous state:', prev[sourceStatus]?.map(q => q.id));
          
          // Create a copy of the questions array and move the question to its new position
          const questionsInColumn = [...prev[sourceStatus]];
          
          if (questionsInColumn.length < 2) {
            console.warn('Not enough questions to reorder');
            return prev;
          }
          
          console.log(`Moving question at index ${sourceIndex} to index ${destinationIndex}`);
          const [movedQuestion] = questionsInColumn.splice(sourceIndex, 1);
          questionsInColumn.splice(destinationIndex, 0, movedQuestion);

          // Update local order_index values to reflect the new order
          questionsInColumn.forEach((question, index) => {
            question.order_index = index;
          });
          
          // Store the new order for emission after state update
          newQuestionIds = questionsInColumn.map(q => q.id.toString());
          console.log('New question order after reorder:', newQuestionIds);
          
          // Create a completely new state object with new array reference for ALL statuses
          const newState = {};
          Object.keys(prev).forEach(key => {
            if (key === sourceStatus) {
              newState[key] = questionsInColumn;
            } else {
              newState[key] = prev[key];
            }
          });
          
          console.log('State update - New state:', newState[sourceStatus]?.map(q => q.id));
          return newState;
        });
        
        // Emit socket event after state update
        if (socket && newQuestionIds) {
          console.log('Emitting reorder with new order:', newQuestionIds);
          socket.emit('reorder-questions', {
            status: sourceStatus,
            questionIds: newQuestionIds,
            sessionId,
            movedQuestionId: questionId.toString()
          });
        }
        
        // Clear tracking after a delay
        setTimeout(() => {
          console.log(`Clearing tracking for ${reorderKey}`);
          recentlyAddedQuestions.current.delete(reorderKey);
        }, 2000);
      }
    }
    
    // Clear drag state after a tiny delay to ensure state update completes
    setTimeout(() => {
      setActiveId(null);
      setActiveQuestion(null);
      setActiveStatus(null);
      setIsHoveringOverDroppable(false);
      setDragPreviewSize({ width: null, height: null });
    }, 0);
  };

  // Handler functions for new features
  const handleTagCreate = async (tagData) => {
    try {
      const response = await api.post(`/api/tags/session/${sessionId}`, tagData);
      setTags([...tags, response.data]);
      setSuccess('Tag created successfully');
    } catch (error) {
      console.error('Error creating tag:', error);
      setError('Failed to create tag');
    }
  };

  const handleTagDelete = async (tagId) => {
    try {
      await api.delete(`/api/tags/${tagId}`);
      setTags(tags.filter(t => t.id !== tagId));
      setSuccess('Tag deleted successfully');
    } catch (error) {
      console.error('Error deleting tag:', error);
      setError('Failed to delete tag');
    }
  };

  const handleCustomColumnCreate = async (columnData) => {
    try {
      let response;
      let updatedColumn;
      
      if (columnData.id) {
        // Update existing column
        response = await api.put(`/api/custom-columns/${columnData.id}`, columnData);
        updatedColumn = response.data;
        
        // Update the column in the list
        setCustomColumns(prev => 
          prev.map(col => col.id === updatedColumn.id ? updatedColumn : col)
        );
        
        setSuccess('Column updated successfully');
      } else {
        // Create new column
        response = await api.post(`/api/custom-columns/session/${sessionId}`, columnData);
        updatedColumn = response.data;
        
        setCustomColumns([...customColumns, updatedColumn]);
        
        // Add the new column to questions state
        setQuestions(prev => ({
          ...prev,
          [`custom_${updatedColumn.id}`]: []
        }));
        
        // Add to column order
        setColumnOrder(prev => [...prev, `custom_${updatedColumn.id}`]);
        
        setSuccess('Custom column created successfully');
      }
    } catch (error) {
      console.error('Error saving column:', error);
      setError(columnData.id ? 'Failed to update column' : 'Failed to create custom column');
    }
  };

  const handleMoveColumnLeft = (status) => {
    const currentIndex = columnOrder.indexOf(status);
    if (currentIndex <= 0) return; // Can't move left from first position
    
    // Don't allow moving past 'new'
    const targetIndex = currentIndex - 1;
    const targetStatus = columnOrder[targetIndex];
    if (targetStatus === 'new') return; // Only 'new' is fixed
    
    // Swap positions
    const newOrder = [...columnOrder];
    [newOrder[currentIndex], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[currentIndex]];
    setColumnOrder(newOrder);
    
    // Save to backend if it's a custom column
    if (status.startsWith('custom_')) {
      saveColumnOrder(newOrder);
    }
  };

  const handleMoveColumnRight = (status) => {
    const currentIndex = columnOrder.indexOf(status);
    if (currentIndex === -1 || currentIndex >= columnOrder.length - 1) return;
    
    // Swap positions
    const newOrder = [...columnOrder];
    [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
    setColumnOrder(newOrder);
    
    // Save to backend if it's a custom column
    if (status.startsWith('custom_')) {
      saveColumnOrder(newOrder);
    }
  };

  const saveColumnOrder = async (newOrder) => {
    try {
      // Extract custom columns and their positions
      const customColumnOrders = newOrder
        .map((status, index) => {
          if (status.startsWith('custom_')) {
            const columnId = parseInt(status.replace('custom_', ''));
            return { id: columnId, order_index: index };
          }
          return null;
        })
        .filter(item => item !== null);
      
      if (customColumnOrders.length > 0) {
        await api.post(`/api/custom-columns/session/${sessionId}/reorder`, { columnOrders: customColumnOrders });
      }
    } catch (error) {
      console.error('Error saving column order:', error);
      setError('Failed to save column order');
    }
  };

  const handleDeleteColumn = async (status, questionCount) => {
    if (questionCount > 0) {
      if (!window.confirm(`This column contains ${questionCount} question(s). All questions will be moved to "New". Continue?`)) {
        return;
      }
    } else {
      if (!window.confirm('Delete this column?')) {
        return;
      }
    }
    
    try {
      if (status.startsWith('custom_')) {
        // Delete custom column
        const columnId = parseInt(status.replace('custom_', ''));
        
        // Move questions to 'new' if any
        if (questionCount > 0) {
          const questionsToMove = questions[status] || [];
          await Promise.all(
            questionsToMove.map(q => 
              api.put(`/api/questions/${q.id}/status`, {
                status: 'new',
                sessionId
              })
            )
          );
        }
        
        // Delete from backend
        await api.delete(`/api/custom-columns/${columnId}`);
        
        // Update local state
        setCustomColumns(customColumns.filter(col => col.id !== columnId));
      } else {
        // Delete system column (selected, done)
        // Move questions to 'new'
        if (questionCount > 0) {
          const questionsToMove = questions[status] || [];
          await Promise.all(
            questionsToMove.map(q => 
              api.put(`/api/questions/${q.id}/status`, {
                status: 'new',
                sessionId
              })
            )
          );
        }
      }
      
      // Remove from column order
      setColumnOrder(prev => prev.filter(col => col !== status));
      
      // Remove from questions state
      setQuestions(prev => {
        const updated = { ...prev };
        // Move questions to 'new'
        if (updated[status]) {
          updated.new = [...(updated.new || []), ...(updated[status] || [])];
          delete updated[status];
        }
        return updated;
      });
      
      setSuccess('Column deleted successfully');
    } catch (error) {
      console.error('Error deleting column:', error);
      setError('Failed to delete column');
    }
  };

  const handleToggleQuestionSelect = (questionId) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
  };

  const handleBulkMoveTo = async (targetStatus) => {
    try {
      const questionIds = Array.from(selectedQuestions);
      
      // Check if trying to move multiple questions to "on_air" or if on_air already has a question
      if (targetStatus === 'on_air') {
        if (questionIds.length > 1) {
          setError('Only one question can be on air at a time. Please select only one question.');
          return;
        }
        
        if (questions.on_air.length > 0 && !selectedQuestions.has(questions.on_air[0].id)) {
          const existingOnAir = questions.on_air[0];
          setError(`Only one question can be on air at a time. Current on-air: "${existingOnAir.content.substring(0, 50)}..."`);
          return;
        }
      }
      
      await Promise.all(
        questionIds.map(qId => 
          api.put(`/api/questions/${qId}/status`, {
            status: targetStatus,
            sessionId
          })
        )
      );
      setSelectedQuestions(new Set());
      setSuccess(`Moved ${questionIds.length} question${questionIds.length > 1 ? 's' : ''} to ${targetStatus}`);
      // Refresh questions
      const response = await api.get(`/api/sessions/${sessionId}/questions`);
      const groupedQuestions = {
        new: [],
        selected: [],
        on_air: [],
        done: []
      };
      
      // Add custom column keys
      customColumns.forEach(column => {
        groupedQuestions[`custom_${column.id}`] = [];
      });
      
      response.data.forEach(question => {
        if (groupedQuestions[question.status] !== undefined) {
          groupedQuestions[question.status].push(question);
        } else {
          // If status doesn't exist, add to 'new'
          groupedQuestions.new.push(question);
        }
      });
      setQuestions(groupedQuestions);
    } catch (error) {
      console.error('Error moving questions:', error);
      setError('Failed to move questions');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedQuestions.size} questions?`)) return;
    
    try {
      const questionIds = Array.from(selectedQuestions);
      await Promise.all(
        questionIds.map(qId => api.delete(`/api/questions/${qId}`))
      );
      setSelectedQuestions(new Set());
      setSuccess(`Deleted ${questionIds.length} questions`);
      // Refresh questions
      const response = await api.get(`/api/sessions/${sessionId}/questions`);
      const groupedQuestions = {
        new: [],
        selected: [],
        on_air: [],
        done: []
      };
      
      // Add custom column keys
      customColumns.forEach(column => {
        groupedQuestions[`custom_${column.id}`] = [];
      });
      
      response.data.forEach(question => {
        if (groupedQuestions[question.status] !== undefined) {
          groupedQuestions[question.status].push(question);
        } else {
          // If status doesn't exist, add to 'new'
          groupedQuestions.new.push(question);
        }
      });
      setQuestions(groupedQuestions);
    } catch (error) {
      console.error('Error deleting questions:', error);
      setError('Failed to delete questions');
    }
  };

  // Filter questions (manual order only)
  const filterAndSortQuestions = (questionsArray) => {
    let filtered = [...questionsArray];

    if (searchTerm) {
      filtered = filtered.filter(q =>
        q.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (q.submitterName && q.submitterName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(q =>
        q.tags && q.tags.some(tag => selectedTags.includes(tag.id))
      );
    }

    return filtered;
  };

  // Pagination helper
  const paginateQuestions = (questionsArray, columnStatus) => {
    const pageSize = 15;
    const currentPage = paginationState[columnStatus] || 0;
    const start = currentPage * pageSize;
    const end = start + pageSize;
    const paginated = questionsArray.slice(start, end);
    const hasMore = end < questionsArray.length;
    
    return { questions: paginated, hasMore, totalCount: questionsArray.length };
  };

  const loadMoreQuestions = (columnStatus) => {
    setPaginationState(prev => ({
      ...prev,
      [columnStatus]: (prev[columnStatus] || 0) + 1
    }));
  };

  const handleQuestionTagsUpdated = async () => {
    // Refresh questions to get updated tags
    try {
      const response = await api.get(`/api/sessions/${sessionId}/questions`);
      const groupedQuestions = {
        new: [],
        selected: [],
        on_air: [],
        done: []
      };
      
      // Add custom column keys
      customColumns.forEach(column => {
        groupedQuestions[`custom_${column.id}`] = [];
      });
      
      response.data.forEach(question => {
        if (groupedQuestions[question.status] !== undefined) {
          groupedQuestions[question.status].push(question);
        } else {
          // If status doesn't exist, add to 'new'
          groupedQuestions.new.push(question);
        }
      });
      
      setQuestions(groupedQuestions);
    } catch (error) {
      console.error('Error refreshing questions:', error);
    }
  };

  // Export questions to Excel
  const handleExportToCSV = () => {
    try {
      // Get all questions from all columns
      const allQuestions = [];
      Object.keys(questions).forEach(status => {
        questions[status].forEach(question => {
          allQuestions.push(question);
        });
      });

      if (allQuestions.length === 0) {
        setError('No questions to export');
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Helper function to get status label
      const getStatusLabel = (status) => {
        if (status === 'new') return 'New';
        if (status === 'selected') return 'Selected';
        if (status === 'on_air') return 'On Air';
        if (status === 'done') return 'Done';
        if (status.startsWith('custom_')) {
          const customColumnId = parseInt(status.replace('custom_', ''));
          const customColumn = customColumns.find(col => col.id === customColumnId);
          return customColumn ? customColumn.name : status;
        }
        return status;
      };

      // Prepare data for Excel export
      const excelData = allQuestions.map(question => {
        const tags = question.tags && question.tags.length > 0
          ? question.tags.map(tag => tag.name).join('; ')
          : '';
        
        const createdDate = new Date(question.created_at);
        const dateStr = createdDate.toLocaleDateString();
        const timeStr = createdDate.toLocaleTimeString();

        return {
          'ID': question.id,
          'Question': question.content || '',
          'Status': getStatusLabel(question.status),
          'Submitter': question.submitterName || question.submitter_name || 'Anonymous',
          'Tags': tags,
          'Created Date': dateStr,
          'Created Time': timeStr
        };
      });

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths for better readability
      const columnWidths = [
        { wch: 8 },  // ID
        { wch: 60 }, // Question
        { wch: 15 }, // Status
        { wch: 20 }, // Submitter
        { wch: 30 }, // Tags
        { wch: 15 }, // Created Date
        { wch: 15 }  // Created Time
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions');

      // Generate filename with date
      const fileName = `questions_session_${sessionId}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Write file with UTF-8 support for special characters
      XLSX.writeFile(workbook, fileName, { 
        bookType: 'xlsx',
        type: 'binary',
        compression: true
      });

      setSuccess(`Exported ${allQuestions.length} questions to Excel`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export questions. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const renderInboxView = () => {
    // Group columns into segments
    const newColumn = { id: 'new', name: 'New', count: questions.new?.length || 0, segment: 'new', canMove: false };
    const selectedColumn = { id: 'selected', name: 'Selected', count: questions.selected?.length || 0, segment: 'selected', canMove: false };
    const onAirColumn = { id: 'on_air', name: 'On Air', count: questions.on_air?.length || 0, segment: 'on_air', canMove: false, canEdit: false, bgColor: 'red' };
    const doneColumn = { id: 'done', name: 'Done', count: questions.done?.length || 0, segment: 'done', canMove: false, canEdit: false, bgColor: 'green' };
    
    // Custom columns from columnOrder
    const customColumnsFromOrder = columnOrder
      .filter(columnId => columnId.startsWith('custom_'))
      .map(columnId => {
        const customColumnId = columnId.replace('custom_', '');
        const customColumn = customColumns.find(col => col.id.toString() === customColumnId);
        if (customColumn) {
          return {
            id: `custom_${customColumn.id}`,
            name: customColumn.name,
            count: questions[`custom_${customColumn.id}`]?.length || 0,
            color: customColumn.color,
            segment: 'custom',
            canMove: true,
            canEdit: true
          };
        }
        return null;
      })
      .filter(Boolean);

    const selectedColumnQuestions = (() => {
      let columnQuestions = questions[selectedInboxColumn] || [];
      
      if (searchTerm) {
        columnQuestions = columnQuestions.filter(q =>
          q.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (q.submitter_name && q.submitter_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (q.submitterName && q.submitterName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (q.tags && q.tags.some(tag => tag.name.toLowerCase().includes(searchTerm.toLowerCase())))
        );
      }
      
      if (selectedTags.length > 0) {
        columnQuestions = columnQuestions.filter(q =>
          q.tags && q.tags.some(tag => selectedTags.includes(tag.id))
        );
      }
      
      return columnQuestions;
    })();
    
    // Helper function to get column name
    const getColumnName = (columnId) => {
      if (columnId === 'new') return 'New';
      if (columnId === 'selected') return 'Selected';
      if (columnId === 'on_air') return 'On Air';
      if (columnId === 'done') return 'Done';
      if (columnId.startsWith('custom_')) {
        const customColumnId = columnId.replace('custom_', '');
        const customColumn = customColumns.find(col => col.id.toString() === customColumnId);
        return customColumn?.name || 'Custom Column';
      }
      return 'Unknown';
    };

    return (
      <DndContext
        key={`inbox-${animationState}`}
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        disabled={animationState === 'expanding' || animationState === 'collapsing'}
        modifiers={[snapCenterToCursor]}
      >
        <div className="flex h-full overflow-hidden">
          {/* Left sidebar - Columns */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col h-full overflow-y-auto">
            <div className="flex-1 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700">QUESTIONS</h3>
                <div className="relative" ref={addDropdownRef}>
                  <button
                    onClick={() => setShowAddDropdown(!showAddDropdown)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                    title="Add new column or question"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  
                  {showAddDropdown && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-40">
                      <button
                        onClick={handleAddColumn}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-t-lg"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Group
                      </button>
                      <button
                        onClick={handleAddQuestionFromDropdown}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-b-lg"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Add Question
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-6">
                {/* New Section */}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">New</div>
                  <div className="space-y-1">
                    <InboxSortableColumn
                      key={newColumn.id}
                      column={newColumn}
                      isSelected={selectedInboxColumn === newColumn.id}
                      onSelect={() => setSelectedInboxColumn(newColumn.id)}
                      isDragActive={!!activeId && !activeId.startsWith('sortable-column-')}
                      onEditColumn={handleEditColumn}
                      onDeleteColumn={handleDeleteInboxColumn}
                      questions={questions}
                      sessionId={sessionId}
                      onHoverChange={handleColumnHoverChange}
                    />
                  </div>
                </div>

                {/* Selected Section */}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Selected</div>
                  <div className="space-y-1">
                    <InboxSortableColumn
                      key={selectedColumn.id}
                      column={selectedColumn}
                      isSelected={selectedInboxColumn === selectedColumn.id}
                      onSelect={() => setSelectedInboxColumn(selectedColumn.id)}
                      isDragActive={!!activeId && !activeId.startsWith('sortable-column-')}
                      onEditColumn={handleEditColumn}
                      onDeleteColumn={handleDeleteInboxColumn}
                      questions={questions}
                      sessionId={sessionId}
                      onHoverChange={handleColumnHoverChange}
                    />
                  </div>
                </div>

                {/* Custom Groups Section */}
                {customColumnsFromOrder.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Custom Groups</div>
                    <SortableContext
                      items={customColumnsFromOrder.map(col => `sortable-column-${col.id}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-1">
                        {customColumnsFromOrder.map((column) => (
                          <InboxSortableColumn
                            key={column.id}
                            column={column}
                            isSelected={selectedInboxColumn === column.id}
                            onSelect={() => setSelectedInboxColumn(column.id)}
                            isDragActive={!!activeId && !activeId.startsWith('sortable-column-')}
                            onEditColumn={handleEditColumn}
                            onDeleteColumn={handleDeleteInboxColumn}
                            questions={questions}
                            sessionId={sessionId}
                            onHoverChange={handleColumnHoverChange}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>
                )}

                {/* On Air Section */}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">On Air</div>
                  <div className="space-y-1">
                    <InboxSortableColumn
                      key={onAirColumn.id}
                      column={onAirColumn}
                      isSelected={selectedInboxColumn === onAirColumn.id}
                      onSelect={() => setSelectedInboxColumn(onAirColumn.id)}
                      isDragActive={!!activeId && !activeId.startsWith('sortable-column-')}
                      onEditColumn={handleEditColumn}
                      onDeleteColumn={handleDeleteInboxColumn}
                      questions={questions}
                      sessionId={sessionId}
                      onHoverChange={handleColumnHoverChange}
                    />
                  </div>
                </div>

                {/* Done Section */}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Done</div>
                  <div className="space-y-1">
                    <InboxSortableColumn
                      key={doneColumn.id}
                      column={doneColumn}
                      isSelected={selectedInboxColumn === doneColumn.id}
                      onSelect={() => setSelectedInboxColumn(doneColumn.id)}
                      isDragActive={!!activeId && !activeId.startsWith('sortable-column-')}
                      onEditColumn={handleEditColumn}
                      onDeleteColumn={handleDeleteInboxColumn}
                      questions={questions}
                      sessionId={sessionId}
                      onHoverChange={handleColumnHoverChange}
                    />
                  </div>
                </div>
              </div>
          </div>
        </div>

        {/* Right side - Questions list */}
        <div className="flex-1 p-6 h-full flex flex-col overflow-y-auto overflow-x-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {getColumnName(selectedInboxColumn)} Questions ({selectedColumnQuestions.length})
            </h2>
          </div>
          
          <div className="space-y-4">
            {selectedColumnQuestions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No questions in this column
              </div>
            ) : (
              <DroppableColumn 
                id={`container-${selectedInboxColumn}`}
                className="space-y-3 min-h-[200px]"
              >
                <SortableContext 
                  items={selectedColumnQuestions.map(q => `${selectedInboxColumn}-${q.id}`)} 
                  strategy={verticalListSortingStrategy}
                >
                  {selectedColumnQuestions.map(question => (
                    <SortableQuestionCard
                      key={question.id}
                      question={question}
                      status={selectedInboxColumn}
                      onEdit={handleEditQuestion}
                      onDelete={handleDeleteQuestion}
                      isSelected={selectedQuestions.has(question.id)}
                      onToggleSelect={handleToggleQuestionSelect}
                      onTagClick={(tagId) => {
                        setSelectedTags(prev => 
                          prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
                        );
                      }}
                      isDragging={activeId === `${selectedInboxColumn}-${question.id}`}
                    />
                  ))}
                </SortableContext>
              </DroppableColumn>
            )}
          </div>
        </div>
        <DragOverlay>
          {activeQuestion ? (
            <div
              className="pointer-events-none flex justify-center items-start"
              style={{
                width: dragPreviewSize.width != null ? Math.max(dragPreviewSize.width * 0.3, 220) : 'auto',
                height: dragPreviewSize.height != null ? Math.max(dragPreviewSize.height * 0.75, 160) : 'auto'
              }}
            >
              <div
                className={`pointer-events-none transition-all duration-200 ease-out ${
                  isHoveringOverDroppable ? 'opacity-80' : 'opacity-100'
                }`}
                style={{
                  transform: 'scale(0.8)',
                  transformOrigin: 'center top',
                  width: '100%',
                  height: '100%'
                }}
              >
                <QuestionCard
                  question={activeQuestion}
                  status={activeStatus}
                  isDragging={true}
                  showCheckbox={false}
                  showActions={false}
                  className="pointer-events-none"
                />
              </div>
            </div>
          ) : null}
        </DragOverlay>
        </div>
      </DndContext>
    );
  };

  const renderListView = () => {
    // Get status label for a given status
    const getStatusLabel = (status) => {
      if (status === 'new') return 'New';
      if (status === 'selected') return 'Selected';
      if (status === 'on_air') return 'On Air';
      if (status === 'done') return 'Done';
      if (status.startsWith('custom_')) {
        const columnId = parseInt(status.replace('custom_', ''));
        const column = customColumns.find(col => col.id === columnId);
        return column ? column.name : status;
      }
      return status;
    };
    
    // Get status color classes for a given status
    const getStatusColorClasses = (status) => {
      if (status === 'on_air') {
        return 'bg-red-100 text-red-800';
      } else if (status === 'selected') {
        return 'bg-yellow-100 text-yellow-800';
      } else if (status === 'done') {
        return 'bg-green-100 text-green-800';
      } else if (status === 'new') {
        return 'bg-blue-100 text-blue-800';
      } else if (status.startsWith('custom_')) {
        const columnId = parseInt(status.replace('custom_', ''));
        const column = customColumns.find(col => col.id === columnId);
        if (column && column.color) {
          // Use inline style for custom colors instead of classes
          return null; // Return null to indicate we need inline styles
        }
      }
      return 'bg-blue-100 text-blue-800'; // Default
    };
    
    // Get inline style for custom column colors
    const getStatusStyle = (status) => {
      if (status.startsWith('custom_')) {
        const columnId = parseInt(status.replace('custom_', ''));
        const column = customColumns.find(col => col.id === columnId);
        if (column && column.color) {
          // Create a lighter background version of the color
          return {
            backgroundColor: column.color + '20', // Add transparency
            color: column.color,
            borderColor: column.color
          };
        }
      }
      return {};
    };
    
    // Collect all questions from all columns in columnOrder
    const allQuestionsWithDuplicates = columnOrder.flatMap(status => {
      const statusQuestions = questions[status] || [];
      return statusQuestions.map(q => ({
        ...q,
        status: status,
        statusLabel: getStatusLabel(status)
      }));
    });
    
    // Deduplicate questions by ID (in case a question appears in multiple statuses)
    const questionMap = new Map();
    allQuestionsWithDuplicates.forEach(q => {
      if (!questionMap.has(q.id)) {
        questionMap.set(q.id, q);
      }
    });
    const allQuestions = Array.from(questionMap.values());

    // Apply filters (search and tags)
    let filteredQuestions = [...allQuestions];
    
    if (searchTerm) {
      filteredQuestions = filteredQuestions.filter(q =>
        q.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (q.submitterName && q.submitterName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (q.submitter_name && q.submitter_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (q.tags && q.tags.some(tag => tag.name.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    if (selectedTags.length > 0) {
      filteredQuestions = filteredQuestions.filter(q =>
        q.tags && q.tags.some(tag => selectedTags.includes(tag.id))
      );
    }
    
    // Apply list view sorting
    const sortedQuestions = [...filteredQuestions].sort((a, b) => {
      let comparison = 0;
      
      switch (listSortBy.field) {
        case 'status':
          comparison = (a.statusLabel || '').localeCompare(b.statusLabel || '');
          break;
        case 'content':
          comparison = (a.content || '').localeCompare(b.content || '');
          break;
        case 'tags':
          const tagsA = a.tags && a.tags.length > 0 ? a.tags.map(t => t.name).join(',') : '';
          const tagsB = b.tags && b.tags.length > 0 ? b.tags.map(t => t.name).join(',') : '';
          comparison = tagsA.localeCompare(tagsB);
          break;
        case 'submitter':
          comparison = (a.submitterName || '').localeCompare(b.submitterName || '');
          break;
        case 'created_at':
          comparison = new Date(a.created_at) - new Date(b.created_at);
          break;
        default:
          comparison = 0;
      }
      
      return listSortBy.order === 'asc' ? comparison : -comparison;
    });
    
    // Handle header click for sorting
    const handleHeaderClick = (field) => {
      setListSortBy(prev => ({
        field,
        order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
      }));
    };
    
    // Render sort indicator
    const renderSortIndicator = (field) => {
      if (listSortBy.field !== field) {
        return (
          <svg className="w-4 h-4 ml-1 text-gray-400 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        );
      }
      
      if (listSortBy.order === 'asc') {
        return (
          <svg className="w-4 h-4 ml-1 text-blue-600 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        );
      } else {
        return (
          <svg className="w-4 h-4 ml-1 text-blue-600 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        );
      }
    };
    
    return (
      <div className="overflow-x-auto overflow-y-auto h-full">
        <table className="w-full bg-white rounded-lg shadow">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestions(new Set(sortedQuestions.map(q => q.id)));
                    } else {
                      setSelectedQuestions(new Set());
                    }
                  }}
                  checked={sortedQuestions.length > 0 && sortedQuestions.every(q => selectedQuestions.has(q.id))}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleHeaderClick('status')}
              >
                Status{renderSortIndicator('status')}
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleHeaderClick('content')}
              >
                Question{renderSortIndicator('content')}
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleHeaderClick('tags')}
              >
                Tags{renderSortIndicator('tags')}
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleHeaderClick('submitter')}
              >
                Submitter{renderSortIndicator('submitter')}
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleHeaderClick('created_at')}
              >
                Date{renderSortIndicator('created_at')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedQuestions.map(question => (
              <tr 
                key={question.id} 
                className={`hover:bg-gray-50 transition-colors ${selectedQuestions.has(question.id) ? 'bg-blue-50' : ''}`}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedQuestions.has(question.id)}
                    onChange={() => handleToggleQuestionSelect(question.id)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-4">
                  <select
                    value={question.status}
                    onChange={async (e) => {
                      const newStatus = e.target.value;
                      
                      // Check if trying to set to "on_air" when another question is already on air
                      if (newStatus === 'on_air' && questions.on_air.length > 0) {
                        const existingOnAir = questions.on_air[0];
                        if (existingOnAir.id !== question.id) {
                          alert(`Only one question can be on air at a time.\n\nCurrent on-air question: "${existingOnAir.content.substring(0, 50)}..."\n\nPlease move it to another status first.`);
                          return;
                        }
                      }
                      
                      await handleStatusChange(question.id, newStatus);
                    }}
                    className={`w-full px-3 py-2 text-sm font-medium rounded border cursor-pointer focus:ring-2 focus:ring-blue-500 ${
                      getStatusColorClasses(question.status) || ''
                    }`}
                    style={getStatusStyle(question.status)}
                  >
                    {columnOrder.map(status => (
                      <option key={status} value={status}>
                        {getStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-4 text-sm text-gray-900 max-w-md truncate">
                  {question.content}
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-wrap gap-1">
                    {question.tags && question.tags.map(tag => (
                      <span
                        key={tag.id}
                        className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-500">
                  {question.submitterName || '-'}
                </td>
                <td className="px-4 py-4 text-sm text-gray-500">
                  <div className="whitespace-nowrap">
                    {new Date(question.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(question.created_at).toLocaleTimeString()}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditQuestion(question)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        setTagModalQuestionId(question.id);
                        setShowQuestionTagModal(true);
                      }}
                      className="text-purple-600 hover:text-purple-800"
                      title="Manage Tags"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredQuestions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No questions found
          </div>
        )}
      </div>
    );
  };

  const renderColumn = (title, status, items, customColor = null) => {
    const isOnAir = status === 'on_air';
    const isNew = status === 'new';
    const isCustomColumn = status.startsWith('custom_');
    const isSystemColumn = ['selected', 'done', 'on_air'].includes(status);
    const canReorder = !isNew; // All columns except New can be reordered
    const canDelete = isCustomColumn || ['selected', 'done'].includes(status); // Custom and system columns (selected, done) can be deleted, but not On Air
    const columnId = `container-${status}`;
    
    // Apply filtering and sorting
    const filteredItems = filterAndSortQuestions(items);
    
    // Apply pagination
    const { questions: paginatedItems, hasMore, totalCount } = paginateQuestions(filteredItems, status);
    
    // Ensure items have unique IDs by filtering out duplicates
    const uniqueItems = paginatedItems.reduce((acc, current) => {
      const x = acc.find(item => item.id === current.id);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);
    
    // Determine border and background colors
    const borderColor = isOnAir ? 'border-red-200' : (isCustomColumn && customColor ? `border-[${customColor}]` : 'border-gray-200');
    const bgColor = isOnAir ? 'bg-red-50' : (isCustomColumn && customColor ? `bg-[${customColor}]/10` : 'bg-gray-50');
    
    return (
      <div className="flex-1 min-w-[350px] max-w-[450px]">
        <div 
          className={`p-4 rounded-lg border-2 ${bgColor} ${borderColor}`}
          style={isCustomColumn && customColor ? { 
            borderColor: customColor,
            backgroundColor: `${customColor}15`
          } : {}}
        >
          <h3 className={`font-semibold mb-3 flex items-center ${isOnAir ? 'text-red-600' : 'text-gray-700'}`}>
            {title === 'New' && (
              <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            )}
            {title === 'Selected' && (
              <svg className="w-5 h-5 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {title === 'On Air' && (
              <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
            {title === 'Done' && (
              <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {isCustomColumn && (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: customColor || '#6B7280' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            )}
            {title}
            <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${
              isOnAir ? 'bg-red-200 text-red-700' : 'bg-gray-200 text-gray-700'
            }`}>
              {uniqueItems.length}
            </span>
            {isOnAir && uniqueItems.length > 0 && (
              <span className="ml-2 text-xs text-red-500">(Only one allowed)</span>
            )}
            {(canReorder || canDelete) && (
              <div className="ml-auto flex items-center space-x-1">
                {canReorder && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveColumnLeft(status);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white rounded"
                      title="Move left"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveColumnRight(status);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white rounded"
                      title="Move right"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteColumn(status, uniqueItems.length);
                    }}
                    className="p-1 text-red-400 hover:text-red-600 hover:bg-white rounded"
                    title="Delete column"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </h3>
          
          <DroppableColumn 
            id={columnId}
            className="space-y-3 min-h-[200px]"
          >
            <SortableContext 
              items={uniqueItems.map(q => `${status}-${q.id}`)} 
              strategy={verticalListSortingStrategy}
            >
              {uniqueItems.map(question => (
                <SortableQuestionCard 
                  key={`${status}-${question.id}`} 
                  question={question} 
                  status={status}
                  onEdit={handleEditQuestion}
                  onDelete={handleDeleteQuestion}
                  isSelected={selectedQuestions.has(question.id)}
                  onToggleSelect={handleToggleQuestionSelect}
                  onTagClick={(tagId) => {
                    setSelectedTags(prev => 
                      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
                    );
                  }}
                  isDragging={activeId === `${status}-${question.id}`}
                />
              ))}
              {uniqueItems.length === 0 && (
                <p className="text-gray-500 text-sm italic">No questions</p>
              )}
            </SortableContext>
            
            {/* Load More Button */}
            {hasMore && (
              <button
                onClick={() => loadMoreQuestions(status)}
                className="mt-3 w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Load More ({totalCount - uniqueItems.length} remaining)
              </button>
            )}
          </DroppableColumn>
          
          {status === 'new' && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <form onSubmit={handleAddQuestion} className="space-y-2">
                <Input
                  type="text"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="Add a new question..."
                />
                <Button 
                  type="submit" 
                  size="sm" 
                  fullWidth 
                  disabled={!newQuestion.trim()}
                >
                  Add Question
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Edit Modal
  const EditModal = () => {
    const textareaRef = useRef(null);
    
    // Focus the textarea when the modal opens
    useEffect(() => {
      if (showEditModal && textareaRef.current) {
        // Set a small timeout to ensure the DOM is fully rendered
        const timeoutId = setTimeout(() => {
          textareaRef.current.focus();
          
          // Place cursor at the end of the text
          const length = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(length, length);
        }, 0);
        
        return () => clearTimeout(timeoutId);
      }
    }, [showEditModal]);
    
    // Maintain focus after each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (showEditModal && textareaRef.current && document.activeElement !== textareaRef.current) {
        // Save current cursor position
        const selectionStart = textareaRef.current.selectionStart;
        const selectionEnd = textareaRef.current.selectionEnd;
        
        // Restore focus and selection
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    });
    
    if (!showEditModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-medium mb-4">Edit Question</h3>
          <div className="mb-4">
            <textarea
              ref={textareaRef}
              className="w-full border border-gray-300 rounded-md p-2 h-32"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Question content"
              // Add onBlur handler to refocus if clicked outside accidentally
              onBlur={(e) => {
                // Only refocus if still editing (modal is open)
                if (showEditModal && !e.currentTarget.contains(e.relatedTarget)) {
                  setTimeout(() => textareaRef.current?.focus(), 0);
                }
              }}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setShowEditModal(false);
                setEditingQuestion(null);
                setEditContent('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleSaveEdit}
              disabled={!editContent.trim()}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Session handling is working correctly now
  
  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }
  
  // Show password authentication if not authenticated
  if (!isAuthenticated) {
    return (
      <PasswordAuth 
        sessionId={sessionId}
        onSuccess={() => {
          setIsAuthenticated(true);
          setIsCheckingAuth(false);
        }}
      />
    );
  }
  
  return (
    <Layout 
      setShowShareModal={setShowShareModal}
      sessionOverride={!session && localSession ? localSession : undefined}
    >
      <MetaTags 
        title={`Session ${sessionId} - AskTide`}
        description="Manage live questions and polls for your broadcast session. Real-time audience interaction with professional Q&A management tools."
        url={window.location.href}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
          <div>
            <h1 className="text-2xl font-bold">Question Manager</h1>
            {/* Password Display */}
            <div className="mt-2 flex items-center space-x-2">
              <span className="text-sm text-gray-600">Management Password:</span>
              <div className="bg-gray-100 border border-gray-300 rounded px-2 py-1 font-mono text-sm font-semibold">
                {localStorage.getItem('sessionPassword') || 'N/A'}
              </div>
              <button
                onClick={() => {
                  const password = localStorage.getItem('sessionPassword');
                  if (password) {
                    navigator.clipboard.writeText(password);
                    setSuccess('Password copied to clipboard!');
                  }
                }}
                className="text-blue-600 hover:text-blue-800 text-sm"
                title="Copy password"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
          <div className="relative">
            <Button 
              variant="primary"
              size="sm"
              onClick={() => setShowCogwheelMenu(!showCogwheelMenu)}
              className="flex items-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Button>
            
            {/* Cogwheel Context Menu */}
            {showCogwheelMenu && (
              <div className="absolute top-12 right-0 bg-white border border-gray-200 rounded-lg shadow-lg py-2 w-48 z-50" data-cogwheel-menu>
                <Link to={`/session/${sessionId}/templates`} className="block">
                  <button
                    onClick={() => setShowCogwheelMenu(false)}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Manage Templates
                  </button>
                </Link>
                <Link to={`/session/${sessionId}/display`} className="block">
                  <button
                    onClick={() => setShowCogwheelMenu(false)}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Display View
                  </button>
                </Link>
                <button
                  onClick={() => {
                    setShowShareModal(true);
                    setShowCogwheelMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share Links
                </button>
              </div>
            )}
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 text-green-700 p-4 rounded-md mb-4">
            {success}
          </div>
        )}


        {/* View Mode Toggle & Action Buttons */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'kanban'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              List
            </button>
            <button
              onClick={() => setViewMode('inbox')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'inbox'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              Inbox
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowSearchPopup(true)}
                className="p-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                title="Search Questions"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              
              {/* Search Context Menu */}
              {showSearchPopup && (
                <div className="absolute top-12 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80 z-50" data-search-popup>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Search Questions
                      </label>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search questions or submitter..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setShowSearchPopup(false);
                        }}
                        className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => setShowSearchPopup(false)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowTagManager(true)}
              className="p-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              title="Manage Tags"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </button>
            <button
              onClick={handleExportToCSV}
              className="p-3 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors"
              title="Export Questions to Excel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <div className="relative" ref={mainAddDropdownRef}>
              <button
                onClick={() => setShowMainAddDropdown(!showMainAddDropdown)}
                className="p-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                title="Add Group or Question"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
              
              {showMainAddDropdown && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-40">
                  <button
                    onClick={handleMainAddColumn}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-t-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Column
                  </button>
                  <button
                    onClick={handleMainAddQuestion}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-b-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Add Question
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Questions Section with Frame */}
        <div className="relative">
          <div 
            ref={questionsSectionRef}
            className={`bg-white border-2 border-gray-200 rounded-lg shadow-lg mb-8 transition-all duration-500 ease-in-out ${
              viewMode === 'kanban' ? 'p-6' : ''
            } ${
              animationState === 'expanding' || animationState === 'fullscreen'
                ? `fixed inset-0 z-50 m-0 rounded-none border-0 shadow-2xl h-screen w-screen ${
                    viewMode === 'kanban' ? 'p-6 overflow-auto' : 
                    viewMode === 'list' ? 'p-0 overflow-auto' : 
                    'p-0 overflow-hidden'
                  }` 
                : animationState === 'collapsing'
                ? `fixed inset-0 z-50 m-0 rounded-none border-0 shadow-2xl h-screen w-screen opacity-0 scale-95 ${
                    viewMode === 'kanban' ? 'p-6 overflow-auto' : 
                    viewMode === 'list' ? 'p-0 overflow-auto' : 
                    'p-0 overflow-hidden'
                  }`
                : ''
            }`}
            style={{
              height: animationState === 'normal' ? questionsSectionHeight : undefined,
              overflow: animationState === 'normal' ? 'auto' : undefined
            }}
          >
          
          {/* Fullscreen Controls - only show in fullscreen mode */}
          {animationState === 'fullscreen' && (
            <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      viewMode === 'kanban'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                    Kanban
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      viewMode === 'list'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                    List
                  </button>
                  <button
                    onClick={() => setViewMode('inbox')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      viewMode === 'inbox'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    Inbox
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <div className="px-4 py-3 rounded-lg bg-blue-500 text-white font-medium">
                    Manual Order
                  </div>
                  
                  <div className="relative">
                    <button
                      onClick={() => setShowSearchPopup(true)}
                      className="p-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                      title="Search Questions"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                    
                    {/* Search Context Menu */}
                    {showSearchPopup && (
                      <div className="absolute top-12 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80 z-50" data-search-popup>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Search Questions
                            </label>
                            <input
                              type="text"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              placeholder="Search questions or submitter..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              autoFocus
                            />
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <button
                              onClick={() => {
                                setSearchTerm('');
                                setShowSearchPopup(false);
                              }}
                              className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
                            >
                              Clear
                            </button>
                            <button
                              onClick={() => setShowSearchPopup(false)}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                            >
                              Search
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setShowTagManager(true)}
                    className="p-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                    title="Manage Tags"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </button>
                  <div className="relative" ref={mainAddDropdownRef}>
                    <button
                      onClick={() => setShowMainAddDropdown(!showMainAddDropdown)}
                      className="p-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                      title="Add Group or Question"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                    
                    {showMainAddDropdown && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-40">
                        <button
                          onClick={handleMainAddColumn}
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-t-lg"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add Group
                        </button>
                        <button
                          onClick={handleMainAddQuestion}
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-b-lg"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          Add Question
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Content area with top padding for fixed header in fullscreen */}
          <div className={animationState === 'fullscreen' ? 'pt-20 h-full flex flex-col' : ''}>
          {loading ? (
            <div className="text-center py-8">
              <p>Loading questions...</p>
            </div>
          ) : viewMode === 'list' ? (
            renderListView()
          ) : viewMode === 'inbox' ? (
            renderInboxView()
          ) : (
            <DndContext
              key={`kanban-${animationState}`}
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              disabled={animationState === 'expanding' || animationState === 'collapsing'}
              modifiers={[snapCenterToCursor]}
            >
              <div className="flex flex-col md:flex-row gap-4 overflow-x-auto overflow-y-auto pb-4 h-full" style={{ overflowX: 'scroll' }}>
                {columnOrder.map((status, index) => {
                  // Handle system columns
                  if (status === 'new') {
                    return <div key={`column-${status}`}>{renderColumn('New', 'new', questions.new)}</div>;
                  } else if (status === 'selected') {
                    return <div key={`column-${status}`}>{renderColumn('Selected', 'selected', questions.selected)}</div>;
                  } else if (status === 'on_air') {
                    return <div key={`column-${status}`}>{renderColumn('On Air', 'on_air', questions.on_air)}</div>;
                  } else if (status === 'done') {
                    return <div key={`column-${status}`}>{renderColumn('Done', 'done', questions.done)}</div>;
                  } else if (status.startsWith('custom_')) {
                    // Handle custom columns
                    const columnId = parseInt(status.replace('custom_', ''));
                    const column = customColumns.find(col => col.id === columnId);
                    if (column) {
                      return (
                        <div key={`column-${status}`}>
                          {renderColumn(
                            column.name,
                            status,
                            questions[status] || [],
                            column.color
                          )}
                        </div>
                      );
                    }
                  }
                  return null;
                })}
              </div>
              
              <DragOverlay>
                {activeId && activeQuestion ? (
                  <QuestionCard
                    question={activeQuestion}
                    status={activeStatus}
                    isDragging={true}
                    showCheckbox={false}
                    showActions={false}
                    className="opacity-90 shadow-lg"
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
          </div>
          </div>
          
          {/* Fade-out overlay and fullscreen button - only for normal state */}
          {animationState === 'normal' && (
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-10">
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 pointer-events-auto">
                <button
                  onClick={handleFullScreenToggle}
                  className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-2 font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  Make Full Screen
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Close Fullscreen Button - only visible in fullscreen mode */}
        {animationState === 'fullscreen' && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <button
              onClick={handleFullScreenToggle}
              className="px-6 py-3 bg-red-600 text-white border-2 border-red-600 rounded-lg shadow-lg hover:bg-red-700 hover:border-red-700 transition-colors flex items-center gap-2 font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Close Fullscreen
            </button>
          </div>
        )}

        {/* Combined QR Code and Reactions Section */}
        <div className="flex items-center my-8">
          <div className="flex-1 border-t border-gray-300"></div>
          <div className="px-4 text-gray-500 text-sm font-medium bg-gray-50 rounded-full py-2 flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            <span>QR Code & Reactions</span>
          </div>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Side-by-side QR Code and Reactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* QR Code Section */}
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                QR Code Display
              </h2>
              <div className="flex items-center space-x-4">
                <Button
                  onClick={() => setShowQRSettingsModal(true)}
                  variant="secondary"
                  className="flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </Button>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-medium ${isQRCodeShowing ? 'text-red-600' : 'text-gray-500'}`}>
                    {isQRCodeShowing ? 'Currently Displayed' : 'Hidden'}
                  </span>
                  <div className={`w-3 h-3 rounded-full ${isQRCodeShowing ? 'bg-red-500 animate-pulse' : 'bg-gray-300'}`}></div>
                </div>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              Display a QR code on screen for audience members to scan and submit questions.
            </p>
            
            <div className="flex flex-col items-center space-y-4">
              <Button 
                variant={isQRCodeShowing ? "primary" : "secondary"}
                size="lg"
                onClick={handleToggleQR}
                className={`px-8 py-4 text-lg font-semibold ${isQRCodeShowing ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/25 animate-pulse' : 'text-white'}`}
                style={!isQRCodeShowing ? { backgroundColor: '#2663eb' } : {}}
                data-umami-event={isQRCodeShowing ? "Hide QR Code" : "Show QR Code"}
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isQRCodeShowing ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  )}
                </svg>
                {isQRCodeShowing ? 'Hide QR Code' : 'Show QR Code'}
              </Button>
              
              {/* Template Mode Toggle - Centered beneath button */}
              {qrTemplate && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">Display Mode:</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${qrTemplate.display_mode === 'fullscreen' ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                      Full Screen
                    </span>
                    <button
                      onClick={handleToggleQRTemplate}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        qrTemplate.display_mode === 'lower-third' ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          qrTemplate.display_mode === 'lower-third' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-sm ${qrTemplate.display_mode === 'lower-third' ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                      Lower Third
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reactions Section */}
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Audience Reactions
            </h2>
            <div className="flex items-center space-x-2">
              <span className={`text-sm font-medium ${reactionsEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                {reactionsEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <div className={`w-3 h-3 rounded-full ${reactionsEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            </div>
          </div>
          
          <p className="text-gray-600 mb-6">
            Allow audience members to react with emojis during the session. 
            Reactions will appear as animated particles on the audience view.
          </p>
          
          <div className="flex flex-col items-center space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Enable Reactions:</span>
              <button
                onClick={handleToggleReactions}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  reactionsEnabled ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    reactionsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {/* Reaction Icons Selection */}
            {reactionsEnabled && (
              <div className="w-full max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Reaction Icons:
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="flex flex-col items-center space-y-2">
                      <div className="w-16 h-16 border-2 border-gray-200 rounded-lg flex items-center justify-center text-2xl bg-gray-50">
                        {reactionIcons[index] || '😊'}
                      </div>
                      <select
                        value={reactionIcons[index] || '😊'}
                        onChange={(e) => {
                          const newIcons = [...reactionIcons];
                          newIcons[index] = e.target.value;
                          handleUpdateReactionIcons(newIcons);
                        }}
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="❤️">❤️ Heart</option>
                        <option value="👍">👍 Thumbs Up</option>
                        <option value="👎">👎 Thumbs Down</option>
                        <option value="😊">😊 Smile</option>
                        <option value="😂">😂 Laugh</option>
                        <option value="😮">😮 Surprised</option>
                        <option value="😢">😢 Sad</option>
                        <option value="😡">😡 Angry</option>
                        <option value="🔥">🔥 Fire</option>
                        <option value="⭐">⭐ Star</option>
                        <option value="🎉">🎉 Party</option>
                        <option value="👏">👏 Clap</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Visual Separator with Collapsible Arrow */}
        <div className="flex items-center my-8">
          <div className="flex-1 border-t border-gray-300"></div>
          <button
            onClick={() => setPollsSectionCollapsed(!pollsSectionCollapsed)}
            className="px-4 text-gray-500 text-sm font-medium bg-gray-50 rounded-full py-2 hover:bg-gray-100 transition-colors flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>Interactive Polls</span>
            <svg 
              className={`w-4 h-4 transition-transform duration-200 ${pollsSectionCollapsed ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Polls Section with Frame */}
        {!pollsSectionCollapsed && (
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Polls
          </h2>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500">
              {polls.length} poll{polls.length !== 1 ? 's' : ''}
            </div>
            <Button
              onClick={() => setShowPollSettingsModal(true)}
              variant="secondary"
              className="flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Settings</span>
            </Button>
            <Button
              onClick={() => {
                setEditingPoll(null);
                setShowPollModal(true);
              }}
              className="flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Create Poll</span>
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {polls.length === 0 ? (
            <div className="col-span-full">
              <div className="bg-white p-8 rounded-lg shadow-md border-2 border-dashed border-gray-300 text-center">
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Create a Poll</h3>
                    <p className="text-gray-500 mt-1">Get real-time feedback from your audience</p>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingPoll(null);
                      setShowPollModal(true);
                    }}
                    className="flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Create Poll</span>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            polls.map(poll => (
              <PollCard
                key={poll.id}
                poll={poll}
                onToggleOnAir={handleTogglePollOnAir}
                onAsk={handleAskPoll}
                onEdit={(poll) => {
                  setEditingPoll(poll);
                  setShowPollModal(true);
                }}
                onDelete={handleDeletePoll}
                onResetResults={handleResetPollResults}
                onToggleDisplayMode={handleTogglePollDisplayMode}
              />
            ))
          )}
        </div>
      </div>
        )}

        {/* Name Tags Section */}
        <div className="flex items-center my-8">
          <div className="flex-1 border-t border-gray-300"></div>
          <button
            onClick={() => setNameTagsSectionCollapsed(!nameTagsSectionCollapsed)}
            className="px-4 text-gray-500 text-sm font-medium bg-gray-50 rounded-full py-2 hover:bg-gray-100 transition-colors flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span>Name Tags</span>
            <svg 
              className={`w-4 h-4 transition-transform duration-200 ${nameTagsSectionCollapsed ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Name Tags Section with Frame */}
        {!nameTagsSectionCollapsed && (
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Name Tags
              </h2>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  {nameTags.length} name tag{nameTags.length !== 1 ? 's' : ''}
                </div>
                <Button
                  onClick={() => setShowNameTagSettingsModal(true)}
                  variant="secondary"
                  className="flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </Button>
                <Button
                  onClick={() => {
                    setEditingNameTag(null);
                    setShowNameTagModal(true);
                  }}
                  className="flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Create Name Tag</span>
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
              {nameTags.length === 0 ? (
                <div className="col-span-full">
                  <div className="bg-white p-8 rounded-lg shadow-md border-2 border-dashed border-gray-300 text-center">
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Create a Name Tag</h3>
                        <p className="text-gray-500 mt-1">Display names and titles on screen</p>
                      </div>
                      <Button
                        onClick={() => {
                          setEditingNameTag(null);
                          setShowNameTagModal(true);
                        }}
                        className="flex items-center space-x-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Create Name Tag</span>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                nameTags.map(nameTag => (
                  <NameTagCard
                    key={nameTag.id}
                    nameTag={nameTag}
                    onToggleOnAir={handleToggleNameTagOnAir}
                    onEdit={(nameTag) => {
                      setEditingNameTag(nameTag);
                      setShowNameTagModal(true);
                    }}
                    onDelete={handleDeleteNameTag}
                    onClone={handleCloneNameTag}
                    onUpdatePlacement={handleUpdateNameTagPlacement}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Flow Grid Section */}
        <div className="flex items-center my-8">
          <div className="flex-1 border-t border-gray-300"></div>
          <div className="px-4 text-gray-500 text-sm font-medium bg-gray-50 rounded-full py-2 flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <span>Flow Grid</span>
          </div>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <svg className="w-6 h-6 mr-2" style={{ color: '#101827' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Flow Grid Display
            </h2>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleFlowGridSettingsUpdate('scrollingEnabled', !flowGridSettings.scrollingEnabled)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700"
                title={flowGridSettings.scrollingEnabled ? 'Pause Scrolling' : 'Play Scrolling'}
              >
                {flowGridSettings.scrollingEnabled ? (
                  <>
                    <FaPause className="w-4 h-4 mr-2" />
                    <span>Pause Scrolling</span>
                  </>
                ) : (
                  <>
                    <FaPlay className="w-4 h-4 mr-2" />
                    <span>Play Scrolling</span>
                  </>
                )}
              </button>
              <Button
                onClick={() => setShowFlowGridSettingsModal(true)}
                variant="secondary"
                className="flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Settings</span>
              </Button>
              <a
                href={`/flow/${sessionId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open Flow Grid
              </a>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Keep Source Column dropdown visible */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Column
              </label>
              <select
                value={flowGridSettings.sourceColumn}
                onChange={(e) => handleFlowGridSettingsUpdate('sourceColumn', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-700 focus:border-gray-700"
              >
                <option value="new">New Questions</option>
                <option value="selected">Selected Questions</option>
                <option value="on_air">On Air Questions</option>
                <option value="done">Done Questions</option>
                {customColumns.map(column => (
                  <option key={column.id} value={column.id.toString()}>
                    {column.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Live Text Section */}
        <div className="flex items-center my-8 cursor-pointer" onClick={() => setShowLiveTextSection(!showLiveTextSection)}>
          <div className="flex-1 border-t border-gray-300"></div>
          <div className="px-4 text-gray-500 text-sm font-medium bg-gray-50 rounded-full py-2 flex items-center space-x-2 hover:bg-gray-100 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Live Text</span>
            <svg 
              className={`w-4 h-4 transition-transform ${showLiveTextSection ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {showLiveTextSection && (
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <svg className="w-6 h-6 mr-2" style={{ color: '#101827' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Live Text Display
              </h2>
              <div className="flex items-center space-x-4">
                <Button
                  onClick={() => setShowLiveTextSettingsModal(true)}
                  variant="secondary"
                  className="flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </Button>
                <a
                  href={`/live-text/${sessionId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open Live Text View
                </a>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Content
                </label>
                <textarea
                  value={liveTextInput}
                  onChange={(e) => setLiveTextInput(e.target.value)}
                  rows={4}
                  placeholder="Enter your text here..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                />
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={handleSendLiveText}
                  className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      
      <ShareLinksModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        sessionId={sessionId}
      />
      
      <EditModal />
      
      <PollModal
        isOpen={showPollModal}
        onClose={() => {
          setShowPollModal(false);
          setEditingPoll(null);
        }}
        onSubmit={handlePollModalSubmit}
        poll={editingPoll}
      />
      
      <PollSettingsModal
        isOpen={showPollSettingsModal}
        onClose={() => setShowPollSettingsModal(false)}
        sessionId={sessionId}
      />
      
      <NameTagModal
        isOpen={showNameTagModal}
        onClose={() => {
          setShowNameTagModal(false);
          setEditingNameTag(null);
        }}
        onSubmit={handleNameTagModalSubmit}
        nameTag={editingNameTag}
        globalSettings={nameTagSettings}
      />
      
      <NameTagSettingsModal
        isOpen={showNameTagSettingsModal}
        onClose={() => setShowNameTagSettingsModal(false)}
        onSubmit={handleNameTagSettingsUpdate}
        settings={nameTagSettings}
      />
      
      <QRSettingsModal
        isOpen={showQRSettingsModal}
        onClose={() => setShowQRSettingsModal(false)}
        onSubmit={handleQRSettingsUpdate}
        qrTemplate={qrTemplate}
        socket={socket}
        sessionId={sessionId}
        onImageUpload={handleQRImageUpload}
      />
      
      <FlowGridSettingsModal
        isOpen={showFlowGridSettingsModal}
        onClose={() => setShowFlowGridSettingsModal(false)}
        onSubmit={handleFlowGridSettingsModalUpdate}
        settings={flowGridSettings}
        onImageUpload={handleImageUpload}
      />
      
      <LiveTextSettingsModal
        isOpen={showLiveTextSettingsModal}
        onClose={() => setShowLiveTextSettingsModal(false)}
        onSubmit={handleLiveTextSettingsModalUpdate}
        settings={liveTextSettings}
      />
      
      {/* Display Preview */}
      <DisplayPreview
        sessionId={sessionId}
        isVisible={showDisplayPreview}
        onToggle={() => setShowDisplayPreview(!showDisplayPreview)}
      />

      {/* Tag Manager Modal */}
      <TagManager
        tags={tags}
        onCreateTag={handleTagCreate}
        onDeleteTag={handleTagDelete}
        isOpen={showTagManager}
        onClose={() => setShowTagManager(false)}
      />


      {/* Custom Column Modal */}
      <CustomColumnModal
        isOpen={showCustomColumnModal}
        onClose={() => {
          setShowCustomColumnModal(false);
          setEditingColumn(null);
        }}
        onCreateColumn={handleCustomColumnCreate}
        existingColumns={customColumns}
        editingColumn={editingColumn}
      />

      {/* Add Question Modal */}
      <AddQuestionModal
        isOpen={showAddQuestionModal}
        onClose={() => setShowAddQuestionModal(false)}
        onAddQuestion={handleAddQuestionFromModal}
        sessionId={sessionId}
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedQuestions.size}
        onMoveToStatus={handleBulkMoveTo}
        availableStatuses={[
          { id: 'new', name: 'New' },
          ...columnOrder.filter(s => s !== 'new' && !s.startsWith('custom_')).map(s => ({
            id: s,
            name: s === 'selected' ? 'Selected' : s === 'on_air' ? 'On Air' : s === 'done' ? 'Done' : s
          }))
        ]}
        customColumns={customColumns}
        onAddTag={() => {
          // TODO: Implement bulk tag assignment modal
          alert('Bulk tag assignment coming soon!');
        }}
        onDelete={handleBulkDelete}
        onClearSelection={() => setSelectedQuestions(new Set())}
      />

      {/* Question Tag Modal */}
      <QuestionTagModal
        isOpen={showQuestionTagModal}
        onClose={() => {
          setShowQuestionTagModal(false);
          setTagModalQuestionId(null);
        }}
        questionId={tagModalQuestionId}
        availableTags={tags}
        currentTags={tagModalQuestionId ? 
          (() => {
            // Include questions from all columns, including custom columns
            const allQuestions = [
              ...questions.new, 
              ...questions.selected, 
              ...questions.on_air, 
              ...questions.done,
              ...Object.keys(questions)
                .filter(key => key.startsWith('custom_'))
                .flatMap(key => questions[key])
            ];
            const question = allQuestions.find(q => q.id === tagModalQuestionId);
            return question?.tags || [];
          })() 
          : []
        }
        onTagsUpdated={handleQuestionTagsUpdated}
      />
      </div>
    </Layout>
  );
};

export default QuestionManager;
 